/**
 * Vue Router configuration
 */
import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { awaitBootGate } from '@/auth/tokenManager'
import { routeToFeature } from '@/utils/featureMap'
import { getAllCatalogPermissions } from '@/components/base/catalogRegistry'
import { PROFILE_GATE_ALLOWED_PREFIXES, profileCompletionGateBlockReason } from './profileCompletionGate'
import { useCompanyContext } from '@/composables/useCompanyContext'
import { resolveFlowField } from '@/composables/useFlow'
// Public legal pages must stay reachable from a technician session (Google Play
// Delete account URL / privacy / cookies). Without this allowlist the tech guard
// redirects every non-/technician path to /technician. Shared with the native
// session repair in App.vue, which must not bounce them either.
import { TECH_SESSION_PUBLIC_PATHS, technicianLearningLocation } from './technicianPublicPaths'
import LoginView from '@/views/LoginView.vue'

// SETTINGS-CATALOG-ACCESS (2026-07-13): the Reference Data hub must open for any
// user who can access at least one reference catalog (not only settings admins).
// The hub itself greys out catalogs the user cannot open; each catalog page keeps
// its own guard, and the backend enforces RBAC per request.
const REFERENCE_DATA_PERMS = [
  ...new Set(['settings.references', 'admin.manage_settings', ...getAllCatalogPermissions()]),
]

// TC-67: cleaning tenants address the worker list as /production/cleaners so the
// URL bar reads the trade instead of "technicians". The `cleaners` alias resolves
// to the same view, so /production/technicians keeps working for every company
// (deep links, bookmarks, voice map). This helper decides where the bare
// /production redirect should land, using the same synchronous preset signal that
// useFlow.isCleaning falls back to (the domain the auth store hydrated from
// localStorage), so the landing URL is right on first navigation without a reload.
const workerListPath = () => {
  try {
    const preset = useAuthStore().domain?.industry_preset_code || null
    return preset === 'cleaning' ? '/production/cleaners' : '/production/technicians'
  } catch (e) {
    return '/production/technicians'
  }
}

const routes = [
  {
    path: '/',
    redirect: '/dashboard'
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginView,
    meta: { requiresAuth: false }
  },
  {
    // Quick-Launch first-run: a calm, ready-to-use Welcome page shown once to a
    // freshly provisioned company owner (standalone, no CRM chrome — see
    // showLayout in App.vue which already hides the shell for name 'Welcome').
    path: '/welcome',
    name: 'Welcome',
    component: () => import('@/views/welcome/QuickLaunchWelcomeView.vue'),
    meta: { requiresAuth: true, title: 'Welcome' }
  },
  {
    path: '/robot',
    name: 'RobotTest',
    component: () => import('@/views/RobotTestView.vue'),
    meta: { requiresAuth: false, title: 'Robot' }
  },
  {
    path: '/dev/menu-icons-preview',
    name: 'MenuIconsPreview',
    component: () => import('@/views/dev/MenuIconsPreview.vue'),
    meta: { requiresAuth: false, title: 'Menu Icons Preview' }
  },
  {
    // Public customer-facing estimate page (secure token link from email/SMS)
    path: '/e/:token',
    name: 'PublicEstimate',
    component: () => import('@/views/public/PublicEstimateView.vue'),
    meta: { requiresAuth: false, title: 'Estimate' }
  },
  {
    // Our own hosted pay page (tips-enabled companies) — token link/QR to the
    // customer. Lets them add a tip before paying by card / bank.
    path: '/pay/:token',
    name: 'PublicPay',
    component: () => import('@/views/public/PublicPayView.vue'),
    meta: { requiresAuth: false, title: 'Pay' }
  },
  {
    // Public self-service "Book online" wizard reached from the company website
    // or its Google Business Profile booking link. chatKey = company public key.
    path: '/book/:chatKey',
    name: 'PublicBooking',
    component: () => import('@/views/public/PublicBookingView.vue'),
    meta: { requiresAuth: false, title: 'Book a service' }
  },
  {
    // FSJ-6-00071 Phase 3 — Door Hanger QR public landing (no auth; permission null).
    path: '/gift/dh/:token',
    name: 'LoyaltyDoorHangerLanding',
    component: () => import('@/modules/marketing/loyalty/public/DoorHangerLandingView.vue'),
    meta: {
      requiresAuth: false,
      permission: null,
      title: 'Gift offer',
      isPortalRoute: true,
    },
  },
  {
    // FSJ-6-00071 Phase 4 — private Client Hub (token ≠ friend public link).
    path: '/gift/hub/:token',
    name: 'LoyaltyReferralHub',
    component: () => import('@/modules/marketing/loyalty/public/ReferralHubView.vue'),
    meta: {
      requiresAuth: false,
      permission: null,
      title: 'Referral Hub',
      isPortalRoute: true,
    },
  },
  {
    // FSJ-6-00071 Phase 4 — public friend gift landing (no Hub access).
    path: '/gift/ref/:token',
    name: 'LoyaltyReferralFriendLanding',
    component: () => import('@/modules/marketing/loyalty/public/ReferralFriendLandingView.vue'),
    meta: {
      requiresAuth: false,
      permission: null,
      title: 'Friend gift',
      isPortalRoute: true,
    },
  },
  {
    path: '/accept-company-invite',
    name: 'AcceptCompanyInvite',
    component: () => import('@/views/AcceptCompanyInviteView.vue'),
    meta: { requiresAuth: false, title: 'Company invitation' }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/ForgotPasswordView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/views/ResetPasswordView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/cookies',
    name: 'CookiePolicy',
    component: () => import('@/views/legal/CookiePolicyView.vue'),
    meta: { requiresAuth: false, title: 'Cookie Policy' }
  },
  {
    path: '/privacy',
    name: 'PrivacyPolicy',
    component: () => import('@/views/legal/PrivacyPolicyView.vue'),
    meta: { requiresAuth: false, title: 'Privacy Policy' }
  },
  {
    path: '/terms',
    name: 'TermsOfService',
    component: () => import('@/views/legal/TermsOfServiceView.vue'),
    meta: { requiresAuth: false, title: 'Terms of Service' }
  },
  {
    path: '/delete-account',
    name: 'DeleteAccount',
    component: () => import('@/views/legal/DeleteAccountView.vue'),
    meta: { requiresAuth: false, title: 'Delete Account' }
  },
  {
    path: '/impersonate',
    name: 'Impersonate',
    component: () => import('@/views/ImpersonateView.vue'),
    meta: { requiresAuth: false }
  },
  // Dialer V2 — standalone softphone popup window (Workiz-style). Opened from
  // the CRM via window.open('/dialer', ...); renders WITHOUT the CRM chrome
  // (App.vue hides the layout for this route). All-new code in src/dialer/.
  {
    path: '/dialer',
    name: 'DialerWindow',
    component: () => import('@/dialer/DialerWindowView.vue'),
    meta: { requiresAuth: true, title: 'Dialer', standalone: true }
  },
  {
    // Public document signing — no login, access by one-time signer token.
    path: '/sign/:token',
    name: 'PublicSign',
    component: () => import('@/modules/esign/public/PublicSignView.vue'),
    meta: { requiresAuth: false, title: 'Sign Document' }
  },
  // Platform God Mode console — hidden route, available ONLY to users with
  // platform_access ('full'/'readonly'). Everyone else is silently redirected
  // to the dashboard, as if the route did not exist.
  {
    path: '/platform/god',
    name: 'PlatformGodMode',
    component: () => import('@/views/platform/PlatformGodModeView.vue'),
    meta: { requiresAuth: true, title: 'Platform Console' },
    beforeEnter: (to, from, next) => {
      const authStore = useAuthStore()
      if (authStore.user?.platform_access) {
        next()
      } else {
        console.log('[Router] /platform/god denied — redirecting to dashboard')
        next('/dashboard')
      }
    }
  },
  // ========== LANDING VOICE (PUBLIC) ==========
  // Telegram Mini App: create a test account from a Telegram chat.
  {
    path: '/tg/new-account',
    name: 'TelegramTestAccount',
    component: () => import('@/views/landing/TelegramTestAccountView.vue'),
    meta: { requiresAuth: false, title: 'New Test Account' }
  },
  {
    path: '/magic/:token',
    name: 'LandingMagicLogin',
    component: () => import('@/views/landing/MagicLoginView.vue'),
    meta: { requiresAuth: false, title: 'Signing you in' }
  },
  {
    path: '/c/:token',
    name: 'LandingTranscript',
    component: () => import('@/views/landing/TranscriptView.vue'),
    meta: { requiresAuth: false, title: 'Conversation' }
  },
  // ========== PUBLIC CLIENT PORTAL (D3) ==========
  {
    path: '/portal/parts/:token',
    name: 'PartsPortalPublic',
    component: () => import('@/modules/portal/views/PartsPortalView.vue'),
    meta: { requiresAuth: false, title: 'Part order status' }
  },
  {
    path: '/nps/:token',
    name: 'PublicNpsSurvey',
    component: () => import('@/modules/crm/views/reputation/PublicNpsSurveyView.vue'),
    meta: { requiresAuth: false, title: 'Survey', isPortalRoute: true }
  },
  {
    path: '/exit-survey/:token',
    name: 'PublicExitSurvey',
    component: () => import('@/modules/hr/views/PublicExitSurveyView.vue'),
    meta: { requiresAuth: false, title: 'Exit Survey', isPortalRoute: true }
  },
  {
    path: '/survey/:token',
    name: 'PublicSurvey',
    component: () => import('@/modules/hr/views/PublicSurveyView.vue'),
    meta: { requiresAuth: false, title: 'Survey', isPortalRoute: true }
  },
  {
    path: '/welcome-form/:token',
    name: 'PublicOnboardingForm',
    component: () => import('@/modules/hr/views/PublicOnboardingFormView.vue'),
    meta: { requiresAuth: false, title: 'Welcome Form', isPortalRoute: true }
  },
  {
    path: '/careers/:domainCode/job/:slug',
    name: 'PublicJobDetail',
    component: () => import('@/modules/hr/views/PublicJobDetailView.vue'),
    meta: { requiresAuth: false, permission: null, title: 'Careers', isPortalRoute: true }
  },
  {
    path: '/careers/:domainCode/:templateCode?',
    name: 'PublicCandidatePortal',
    component: () => import('@/modules/hr/views/PublicCandidatePortalView.vue'),
    meta: { requiresAuth: false, permission: null, title: 'Careers', isPortalRoute: true }
  },
  // ========== TENANT ONBOARDING (PUBLIC) ==========
  {
    path: '/onboarding',
    name: 'IntakeStart',
    component: () => import('@/modules/intake/views/IntakeStartView.vue'),
    meta: { requiresAuth: false, title: 'Onboarding' }
  },
  {
    path: '/onboarding/:token',
    name: 'IntakePortal',
    component: () => import('@/modules/intake/views/IntakePortalView.vue'),
    meta: { requiresAuth: false, title: 'Onboarding portal', isPortalRoute: true }
  },
  // ========== TENANT ONBOARDING (ADMIN) ==========
  {
    path: '/admin/intake/applications',
    name: 'IntakeAdminList',
    component: () => import('@/modules/intake/views/IntakeAdminListView.vue'),
    meta: { requiresAuth: true, title: 'Onboarding applications', permission: 'admin.intake.view' }
  },
  {
    path: '/admin/intake/applications/:id',
    name: 'IntakeAdminDetail',
    component: () => import('@/modules/intake/views/IntakeAdminDetailView.vue'),
    meta: { requiresAuth: true, title: 'Onboarding application', permission: 'admin.intake.view' }
  },
  {
    path: '/admin/intake/schema',
    name: 'IntakeAdminSchema',
    component: () => import('@/modules/intake/views/IntakeAdminSchemaView.vue'),
    meta: { requiresAuth: true, title: 'Edit questionnaire', permission: 'admin.intake.manage_schema' }
  },
  // ========== DASHBOARD MODULE (Personal Widget Board) ==========
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: () => import('@/views/dashboard/DashboardBoardView.vue'),
    meta: { requiresAuth: true, title: 'Dashboard', permission: 'dashboard.view' }
  },
  {
    path: '/dashboard/classic',
    name: 'DashboardClassic',
    component: () => import('@/views/dashboard/MainDashboardView.vue'),
    meta: { requiresAuth: true, title: 'Dashboard (Classic)', permission: 'dashboard.view' }
  },
  {
    path: '/dashboard/technical',
    name: 'TechnicalDashboard',
    component: () => import('@/views/dashboard/TechnicalDashboardView.vue'),
    meta: { requiresAuth: true, title: 'Technical Dashboard', permission: 'dashboard.view' }
  },
  {
    path: '/dashboard/settings',
    name: 'DashboardSettings',
    component: () => import('@/views/dashboard/PersonalSettingsView.vue'),
    meta: { requiresAuth: true, title: 'Personal Settings', permission: 'dashboard.view' }
  },
  {
    // Tutorial v2 — isolated new-design coaching-practice screen (Artem Figma
    // "1.0 Tutorial"). Purely additive, front-end-only; no existing screen is
    // touched. hideModuleSubmenu keeps it immersive (no module flyout).
    path: '/tutorial-v2',
    name: 'TutorialV2',
    component: () => import('@/views/TutorialV2View.vue'),
    meta: { requiresAuth: true, title: 'Tutorial v2', permission: null, hideModuleSubmenu: true }
  },
  {
    path: '/appearance',
    name: 'Appearance',
    component: () => import('@/modules/appearance/AppearanceView.vue'),
    meta: { requiresAuth: true, title: 'Appearance' }
  },
  {
    path: '/appearance/design-map',
    name: 'DesignMap',
    component: () => import('@/modules/appearance/DesignMapView.vue'),
    meta: { requiresAuth: true, title: 'Design Map' }
  },
  // Legacy URL — consolidated into the unified Appearance section.
  {
    path: '/account/my-theme',
    redirect: '/appearance'
  },
  // ========== CALL CENTER MODULE ==========
  // TICK-00010: "My Phone" page (CallCenterView.vue) hidden — it duplicated
  // Contact Center settings and Inbound Calls views. Old links/bookmarks and
  // voice navigation land on Call Logs instead. The .vue file is kept on disk.
  // {
  //   path: '/call-center',
  //   name: 'CallCenter',
  //   component: () => import('@/views/callcenter/CallCenterView.vue'),
  //   meta: { requiresAuth: true, title: 'Call Center', permission: 'dialer.view' }
  // },
  {
    path: '/call-center',
    redirect: '/call-center/logs'
  },
  {
    path: '/call-center/logs',
    name: 'CallCenterLogs',
    component: () => import('@/views/callcenter/CallLogsView.vue'),
    meta: { requiresAuth: true, title: 'Call Logs', permission: 'dialer.view' }
  },
  {
    // Call Feed v2 — isolated new-design screen (Artem Figma "1.0 Call feed").
    // Purely additive; /call-center/logs (CallLogsView) is untouched. Reuses the
    // same GET /dialer/logs/ data source and the same 'dialer.view' permission.
    path: '/call-center/feed-v2',
    name: 'CallFeedV2',
    component: () => import('@/views/callcenter/CallFeedV2View.vue'),
    meta: { requiresAuth: true, title: 'Call Feed v2', permission: 'dialer.view', hideModuleSubmenu: true }
  },
  {
    path: '/call-center/monitor',
    name: 'CallCenterMonitor',
    component: () => import('@/views/CallCenterMonitorView.vue'),
    meta: { requiresAuth: true, title: 'Call Center Monitor', permission: 'dialer.monitor' }
  },
  {
    path: '/call-center-monitor',  // Backward compatibility redirect
    redirect: '/call-center/monitor'
  },
  {
    // TICK-00059: standalone office "Messages" portal (inbound SMS inbox / chats).
    // Reachable by call-center monitors OR office staff with communications.sms.view.
    path: '/communications/messages',
    name: 'InboundSmsInbox',
    component: () => import('@/views/communications/InboundSmsView.vue'),
    meta: { requiresAuth: true, title: 'Messages', permission: ['dialer.monitor', 'communications.sms.view'] }
  },
  {
    // AP-7-00370 Phase 1: Unified Inbox — one conversation list across SMS +
    // web chat + Inbound Journal call/form events (+ LSA when permitted).
    // Same view permission contract as the Messages portal.
    path: '/communications/inbox',
    name: 'UnifiedInbox',
    component: () => import('@/views/communications/UnifiedInboxView.vue'),
    meta: { requiresAuth: true, title: 'Unified Inbox', permission: ['dialer.monitor', 'communications.sms.view'] }
  },
  {
    // Marketplace AI auto-responder — operator inbox across every connected
    // marketplace (Yelp, Thumbtack, Google LSA, Angi, Facebook, Instagram):
    // see the AI conversations, take over, reply by chat/SMS, or call-bridge.
    path: '/communications/marketplace-inbox',
    name: 'MarketplaceInbox',
    component: () => import('@/views/communications/MarketplaceInboxView.vue'),
    meta: { requiresAuth: true, title: 'Marketplace Inbox', permission: 'integrations.marketing.view' }
  },
  {
    path: '/call-center/flow-editor',
    name: 'CallFlowEditor',
    component: () => import('@/modules/callcenter/CallFlowEditorView.vue'),
    meta: { requiresAuth: true, title: 'Call Flow Editor', permission: 'dialer.manage_flows' }
  },
  {
    path: '/call-center/ivr-constructor',
    name: 'IvrConstructor',
    component: () => import('@/modules/ivr/IvrConstructorView.vue'),
    meta: { requiresAuth: true, title: 'IVR Constructor', permission: 'call_flows.view' }
  },
  {
    path: '/call-center/ivr-constructor/flow/:id',
    name: 'IvrFlowEditor',
    component: () => import('@/modules/ivr/FlowEditorView.vue'),
    meta: { requiresAuth: true, title: 'IVR Flow Editor', permission: 'call_flows.edit' }
  },
  {
    path: '/call-center/settings',
    name: 'CallCenterSettings',
    component: () => import('@/views/CallCenterSettingsView.vue'),
    meta: { requiresAuth: true, title: 'Call Center Settings', permission: ['dialer.view_settings', 'dialer.manage_settings'] }
  },
  {
    path: '/call-center/groups',
    name: 'CallGroups',
    component: () => import('@/views/callcenter/CallGroupsView.vue'),
    meta: { requiresAuth: true, title: 'Call Groups', permission: 'call_groups.view' }
  },
  {
    path: '/call-center/routing',
    name: 'RoutingRules',
    component: () => import('@/views/callcenter/RoutingRulesView.vue'),
    meta: { requiresAuth: true, title: 'Routing Rules', permission: 'routing_rules.view' }
  },
  {
    path: '/call-center/next-steps',
    name: 'NextSteps',
    component: () => import('@/views/callcenter/NextStepView.vue'),
    meta: { requiresAuth: true, title: 'Next Steps', permission: 'next_steps.view' }
  },
  {
    path: '/call-center/voicemails',
    name: 'Voicemails',
    component: () => import('@/views/callcenter/VoicemailsView.vue'),
    meta: { requiresAuth: true, title: 'Voicemail', permission: 'voicemail.view' }
  },
  {
    path: '/call-center/reports',
    name: 'CallCenterReports',
    component: () => import('@/views/callcenter/CallCenterReportsView.vue'),
    meta: { requiresAuth: true, title: 'Reports', permission: 'dialer.view_reports' }
  },
  {
    path: '/call-center/twilio-costs',
    name: 'TwilioCostsReport',
    component: () => import('@/views/callcenter/CallCenterCostsView.vue'),
    meta: { requiresAuth: true, title: 'Telephony Costs', permission: 'dialer.view_billing' }
  },
  {
    path: '/call-center/diagnostics',
    name: 'DiagnosticsLogs',
    component: () => import('@/views/callcenter/DiagnosticsLogsView.vue'),
    meta: { requiresAuth: true, title: 'Diagnostics Logs', permission: 'dialer.manage_settings' }
  },
  {
    path: '/call-center/dialer-health',
    name: 'DialerHealth',
    component: () => import('@/views/callcenter/DialerHealthView.vue'),
    meta: { requiresAuth: true, title: 'Dialer Health', permission: 'dialer.manage_settings' }
  },
  {
    path: '/chat',
    name: 'Chat',
    component: () => import('@/views/ChatView.vue'),
    meta: { requiresAuth: true, title: 'Chat' }
  },
  {
    path: '/settings',
    name: 'Settings',
    redirect: '/settings/administration',
    meta: { requiresAuth: true, title: 'Settings' },
    children: [
      {
        path: 'administration',
        name: 'SettingsAdministration',
        component: () => import('@/views/AdministrationView.vue'),
        meta: { title: 'Administration', permission: 'settings.administration' }
      },
      {
        path: 'onboarding',
        name: 'SettingsOnboarding',
        component: () => import('@/modules/settings/onboarding/OnboardingView.vue'),
        meta: { title: 'Company Onboarding', permission: 'settings.administration' }
      },
      {
        // MD-4-00011: "Service areas & zones" used to open a mock settings page
        // (fake counts, a leaked map key, no real technicians/zones). Send it to
        // the real Technician Zones workspace (technicians sidebar + zone map),
        // which is exactly what this onboarding step is about.
        path: 'territories',
        name: 'SettingsTerritories',
        redirect: '/production/technician-zones'
      },
      {
        path: 'roles',
        name: 'SettingsRoles',
        component: () => import('@/views/RolesView.vue'),
        meta: { title: 'Roles Management', permission: 'settings.roles' }
      },
      {
        path: 'domain',
        name: 'SettingsDomain',
        component: () => import('@/views/DomainSettingsView.vue'),
        meta: { title: 'Company Settings', permission: 'settings.domain' }
      },
      {
        path: 'flow',
        name: 'SettingsFlow',
        component: () => import('@/views/FlowConstructorView.vue'),
        meta: { title: 'Company Flow', permission: 'settings.domain' }
      },
      {
        // Open to every authenticated user: the "My notifications" tab is for
        // everyone; the "Company rules" tab is gated inside the view by
        // admin.manage_settings. No blocking route permission on purpose.
        path: 'notifications',
        name: 'SettingsNotifications',
        component: () => import('@/modules/settings/notifications/NotificationSettingsView.vue'),
        meta: { title: 'Notifications' }
      },
      {
        path: 'branding',
        name: 'SettingsBranding',
        redirect: '/appearance'
      },
      {
        path: 'presets',
        name: 'SettingsPresets',
        component: () => import('@/modules/settings/presets/PresetsView.vue'),
        meta: { title: 'Industry Preset', permission: 'settings.administration' }
      },
      {
        path: 'email-templates',
        name: 'SettingsEmailTemplates',
        component: () => import('@/modules/settings/email-templates/EmailTemplatesView.vue'),
        meta: { title: 'Email Templates', permission: 'email_templates.view' }
      },
      {
        path: 'users',
        name: 'SettingsUsers',
        component: () => import('@/views/UsersView.vue'),
        meta: { title: 'Users Management', permission: 'settings.users' }
      },
      {
        path: 'profile',
        name: 'SettingsProfile',
        component: () => import('@/views/UserProfileView.vue'),
        meta: { title: 'My Profile' }
      },
      {
        path: 'registration-requests',
        name: 'SettingsRegistrationRequests',
        component: () => import('@/views/RegistrationRequestsView.vue'),
        meta: { title: 'Registration Requests', permission: 'admin.manage_users' }
      },
      {
        path: 'voice-robots',
        name: 'SettingsVoiceRobots',
        component: () => import('@/views/VoiceRobotsSettingsView.vue'),
        meta: { title: 'Voice Robots', permission: 'settings.voice_robots' }
      },
      {
        path: 'voice-control-center',
        name: 'SettingsVoiceControlCenter',
        component: () => import('@/views/VoiceRobotControlCenterView.vue'),
        meta: { title: 'Voice Robot Control Center', permission: 'settings.voice_robots' }
      },
      {
        path: 'phone-agent',
        name: 'SettingsPhoneAgent',
        component: () => import('@/views/PhoneAgentView.vue'),
        meta: { title: 'AI Phone Agent', permission: 'settings.voice_robots' }
      },
      {
        path: 'voice-analytics',
        name: 'SettingsVoiceAnalytics',
        component: () => import('@/views/VoiceAnalyticsView.vue'),
        meta: { title: 'Voice Analytics', permission: 'settings.voice_robots' }
      },
      {
        path: 'voice-history',
        name: 'SettingsVoiceHistory',
        component: () => import('@/views/VoiceCommandHistoryView.vue'),
        meta: { title: 'Voice Command History', permission: 'settings.voice_robots' }
      },
      {
        path: 'voice-conversations',
        name: 'SettingsVoiceConversations',
        component: () => import('@/views/VoiceConversationsView.vue'),
        meta: { title: 'Voice Conversations', permission: 'settings.voice_robots' }
      },
      {
        path: 'widget-labs',
        name: 'SettingsWidgetLabs',
        component: () => import('@/modules/widgets/WidgetsView.vue'),
        meta: { title: 'Widget Labs', permission: 'widgets.view' }
      },
      {
        path: 'activity-logs',
        name: 'SettingsActivityLogs',
        component: () => import('@/views/ActivityLogsView.vue'),
        meta: { title: 'Activity Logs', permission: 'admin.view_activity_logs' }
      },
      {
        path: 'operator-status-logs',
        name: 'SettingsOperatorStatusLogs',
        component: () => import('@/views/OperatorStatusLogsView.vue'),
        meta: { title: 'Operator Status Logs', permission: 'admin.view_activity_logs' }
      },
      {
        path: 'concurrent-logins',
        name: 'SettingsConcurrentLogins',
        component: () => import('@/views/ConcurrentLoginsView.vue'),
        meta: { title: 'Concurrent IP Logins', permission: 'admin.view_activity_logs' }
      },
      {
        path: 'references',
        name: 'SettingsReferences',
        component: () => import('@/views/ReferenceSettingsView.vue'),
        meta: { title: 'Reference Data', permission: REFERENCE_DATA_PERMS }
      },
      {
        path: 'references/equipment-types',
        name: 'SettingsEquipmentTypes',
        component: () => import('@/views/EquipmentTypesSettingsView.vue'),
        meta: { title: 'Equipment Types', permission: ['settings.references', 'admin.manage_settings'] }
      },
      {
        path: 'call-center',
        name: 'SettingsCallCenter',
        component: () => import('@/views/CallCenterSettingsView.vue'),
        meta: { title: 'Call Center Settings', permission: 'dialer.manage_settings' }
      },
      {
        path: 'integrations/servicetitan',
        name: 'SettingsServiceTitan',
        component: () => import('@/views/ServiceTitanSettingsView.vue'),
        meta: { title: 'ServiceTitan Integration', permission: 'admin.manage_settings' }
      },
      {
        path: 'integrations/callrail',
        name: 'SettingsCallRail',
        component: () => import('@/views/CallRailSettingsView.vue'),
        meta: { title: 'CallRail Integration', permission: 'admin.manage_settings' }
      },
      {
        path: 'integrations/lsa',
        name: 'SettingsLSA',
        component: () => import('@/views/LSAIntegrationSettingsView.vue'),
        meta: { title: 'Google LSA Integration', permission: 'lsa.settings.view' }
      },
      {
        path: 'integrations/google-reviews',
        name: 'SettingsGoogleReviews',
        component: () => import('@/views/GoogleReviewsIntegrationView.vue'),
        meta: { title: 'Google Reviews', permission: 'integrations.google_reviews.view' }
      },
      {
        path: 'integrations/google-ads-conversions',
        name: 'SettingsGoogleAdsConversions',
        component: () => import('@/views/GoogleAdsConversionsSettingsView.vue'),
        meta: { title: 'Google Ads Conversions', permission: 'integrations.google_ads_conversions.view' }
      },
      {
        path: 'booking-flows',
        name: 'SettingsBookingFlows',
        component: () => import('@/views/settings/BookingFlowBuilderView.vue'),
        meta: { title: 'Online Booking Flows', permission: 'booking.flow.view' }
      },
      {
        path: 'integrations/hurma',
        name: 'SettingsHurmaStaging',
        component: () => import('@/views/HurmaStagingExportView.vue'),
        meta: { title: 'Hurma Staging Export', permission: 'settings.integrations' }
      },
      {
        path: 'integrations/workiz',
        name: 'SettingsWorkiz',
        component: () => import('@/views/WorkizIntegrationView.vue'),
        meta: { title: 'Workiz Integration', permission: 'settings.integrations' }
      },
      {
        path: 'integrations/square',
        name: 'SettingsSquare',
        component: () => import('@/views/SquareIntegrationView.vue'),
        meta: { title: 'Square Integration', permission: 'settings.integrations' }
      },
      {
        path: 'integrations/wave',
        name: 'SettingsWave',
        component: () => import('@/views/WaveIntegrationView.vue'),
        meta: { title: 'Wave Integration', permission: 'settings.integrations' }
      },
      {
        path: 'integrations/iwallet',
        name: 'SettingsIWallet',
        component: () => import('@/views/IWalletIntegrationView.vue'),
        meta: { title: 'iWallet Integration', permission: 'settings.integrations' }
      },
      {
        path: 'integrations/venmo',
        name: 'SettingsVenmo',
        component: () => import('@/views/ManualPaymentsIntegrationView.vue'),
        meta: { title: 'Venmo Integration', permission: 'integrations.manual_payments.view', provider: 'venmo' }
      },
      {
        path: 'integrations/zelle',
        name: 'SettingsZelle',
        component: () => import('@/views/ManualPaymentsIntegrationView.vue'),
        meta: { title: 'Zelle Integration', permission: 'integrations.manual_payments.view', provider: 'zelle' }
      },
      {
        path: 'integrations/zyratalk',
        name: 'SettingsZyraTalk',
        component: () => import('@/views/ZyraTalkIntegrationView.vue'),
        meta: { title: 'ZyraTalk Integration', permission: 'settings.integrations' }
      },
      {
        path: 'integrations/nicejob',
        name: 'SettingsNiceJob',
        component: () => import('@/views/NiceJobIntegrationView.vue'),
        meta: { title: 'NiceJob Integration', permission: 'settings.integrations' }
      },
      {
        path: 'integrations',
        name: 'SettingsIntegrationsHub',
        component: () => import('@/views/IntegrationsHubView.vue'),
        meta: { title: 'Integrations', permission: 'settings.integrations' }
      },
      {
        path: 'integrations/jobber',
        name: 'SettingsJobber',
        component: () => import('@/views/JobberIntegrationView.vue'),
        meta: { title: 'Jobber Integration', permission: 'settings.integrations' }
      },
      {
        path: 'integrations/jobber/data',
        name: 'SettingsJobberData',
        component: () => import('@/views/JobberDataViewerView.vue'),
        meta: { title: 'Jobber Data Viewer', permission: 'settings.integrations' }
      },
      {
        path: 'integrations/housecall',
        name: 'SettingsHousecall',
        component: () => import('@/views/HousecallIntegrationView.vue'),
        meta: { title: 'Housecall Pro Integration', permission: 'settings.integrations' }
      },
      {
        path: 'integrations/housecall/data',
        name: 'SettingsHousecallData',
        component: () => import('@/views/HousecallDataViewerView.vue'),
        meta: { title: 'Housecall Pro Data Viewer', permission: 'settings.integrations' }
      },
      {
        path: 'integrations/leadwinner',
        name: 'SettingsLeadwinner',
        component: () => import('@/views/LeadwinnerIntegrationView.vue'),
        meta: { title: 'LeadWinner Integration', permission: 'integrations.leadwinner.view' }
      },
      {
        path: 'integrations/company-email',
        name: 'SettingsCompanyEmail',
        component: () => import('@/views/CompanyEmailIntegrationView.vue'),
        meta: { title: 'Company Email Intake', permission: 'integrations.company_email.view' }
      },
      // Marketing marketplaces (multi-account) — shared permission pair
      {
        path: 'integrations/angi',
        name: 'SettingsAngi',
        component: () => import('@/views/AngiIntegrationView.vue'),
        meta: { title: 'Angi Integration', permission: 'integrations.marketing.view' }
      },
      {
        path: 'integrations/thumbtack',
        name: 'SettingsThumbtack',
        component: () => import('@/views/ThumbtackIntegrationView.vue'),
        meta: { title: 'Thumbtack Integration', permission: 'integrations.marketing.view' }
      },
      {
        path: 'integrations/yelp',
        name: 'SettingsYelp',
        component: () => import('@/views/YelpIntegrationView.vue'),
        meta: { title: 'Yelp Integration', permission: 'integrations.marketing.view' }
      },
      {
        path: 'integrations/facebook',
        name: 'SettingsFacebook',
        component: () => import('@/views/FacebookIntegrationView.vue'),
        meta: { title: 'Facebook Integration', permission: 'integrations.marketing.view' }
      },
      {
        path: 'integrations/instagram',
        name: 'SettingsInstagram',
        component: () => import('@/views/InstagramIntegrationView.vue'),
        meta: { title: 'Instagram Integration', permission: 'integrations.marketing.view' }
      },
      {
        path: 'integrations/service-direct',
        name: 'SettingsServiceDirect',
        component: () => import('@/views/PaperLeadIntegrationView.vue'),
        props: { providerCode: 'service_direct' },
        meta: { title: 'Service Direct Integration', permission: 'integrations.marketing.view' }
      },
      {
        path: 'integrations/elocal',
        name: 'SettingsELocal',
        component: () => import('@/views/PaperLeadIntegrationView.vue'),
        props: { providerCode: 'elocal' },
        meta: { title: 'eLocal Integration', permission: 'integrations.marketing.view' }
      },
      {
        path: 'integrations/eztexting',
        name: 'SettingsEzTexting',
        component: () => import('@/views/EzTextingIntegrationView.vue'),
        meta: { title: 'EZ Texting Integration', permission: 'settings.integrations' }
      },
      {
        path: 'integrations/ringcentral',
        name: 'SettingsRingCentral',
        component: () => import('@/views/RingCentralSettingsView.vue'),
        meta: { title: 'RingCentral Integration', permission: 'settings.integrations' }
      },
      {
        path: 'integrations/st-jobs',
        name: 'SettingsSTJobs',
        component: () => import('@/views/ServiceTitanJobsReport.vue'),
        meta: { title: 'ServiceTitan Jobs Report', permission: 'admin.manage_settings' }
      },
      {
        path: 'integrations/st-sync-logs',
        name: 'SettingsSTSyncLogs',
        component: () => import('@/views/ServiceTitanSyncLogs.vue'),
        meta: { title: 'ServiceTitan Sync Logs', permission: 'admin.manage_settings' }
      },
      {
        path: 'integrations/st-webhooks',
        name: 'SettingsSTWebhooks',
        component: () => import('@/views/ServiceTitanWebhooksView.vue'),
        meta: { title: 'ServiceTitan Webhooks', permission: 'admin.manage_settings' }
      },
      // Legacy route redirect
      {
        path: 'servicetitan',
        redirect: '/settings/integrations/servicetitan'
      },
      // System Monitoring
      {
        path: 'server-performance',
        name: 'SettingsServerPerformance',
        component: () => import('@/views/ServerPerformanceView.vue'),
        meta: { title: 'Server Performance', permission: 'system.view' }
      },
      {
        path: 'system-health',
        name: 'SettingsSystemHealth',
        component: () => import('@/views/SystemHealthView.vue'),
        meta: { title: 'System Health', permission: 'system.view_metrics' }
      },
      {
        path: 'error-monitor',
        name: 'SettingsErrorMonitor',
        component: () => import('@/views/ErrorMonitorView.vue'),
        meta: { title: 'Error Monitor', permission: 'system.view' }
      },
      {
        path: 'cron-jobs',
        name: 'SettingsCronJobs',
        component: () => import('@/views/CronJobsView.vue'),
        meta: { title: 'Cron Jobs', permission: 'system.manage_cron' }
      },
      {
        path: 'documentation',
        name: 'SettingsDocumentation',
        component: () => import('@/views/DocumentationView.vue'),
        meta: { title: 'Documentation', permission: 'docs.view' }
      },
      // IT Infrastructure (Protected)
      {
        path: 'it-infrastructure',
        name: 'SettingsITInfrastructure',
        component: () => import('@/modules/assets/views/it/ITDashboardView.vue'),
        meta: { title: 'IT Infrastructure', permission: 'settings.it_infrastructure' }
      },
      {
        path: 'it-infrastructure/resources',
        name: 'SettingsITResources',
        component: () => import('@/modules/assets/views/it/ITResourcesView.vue'),
        meta: { title: 'IT Resources', permission: 'settings.it_infrastructure' }
      },
      {
        path: 'it-infrastructure/vault',
        name: 'SettingsSecureVault',
        component: () => import('@/modules/assets/views/it/SecureVaultView.vue'),
        meta: { title: 'Secure Vault', permission: 'vault.view' }
      },
      {
        path: 'it-infrastructure/subscriptions',
        name: 'SettingsITSubscriptions',
        component: () => import('@/modules/assets/views/it/ITSubscriptionsView.vue'),
        meta: { title: 'IT Subscriptions', permission: 'settings.it_infrastructure' }
      },
      {
        path: 'it-infrastructure/categories',
        name: 'SettingsITCategories',
        component: () => import('@/modules/assets/views/it/ITCategoriesView.vue'),
        meta: { title: 'IT Categories', permission: 'settings.it_infrastructure' }
      },
      {
        path: 'task-types',
        name: 'SettingsTaskTypes',
        component: () => import('@/views/settings/SettingsTaskTypes.vue'),
        meta: { title: 'Task Types', permission: 'task_types.view' }
      },
      {
        path: 'quality-issues',
        name: 'SettingsQualityIssues',
        component: () => import('@/views/settings/SettingsQualityIssues.vue'),
        meta: { title: 'Quality Control', permission: 'quality.view' }
      },
      {
        path: 'discount-presets',
        name: 'SettingsDiscountPresets',
        component: () => import('@/views/settings/SettingsDiscountPresets.vue'),
        meta: { title: 'Discount Presets', permission: 'invoices.apply_discount' }
      },
      {
        path: 'customer-portal',
        name: 'SettingsCustomerPortal',
        component: () => import('@/views/CustomerPortalSettingsView.vue'),
        meta: { title: 'Customer Portal Settings', permission: 'settings.administration' }
      }
    ]
  },
  // ========== PULSE MODULE (Company Health Analytics) ==========
  {
    path: '/pulse',
    name: 'Pulse',
    component: () => import('@/modules/pulse/views/PulseView.vue'),
    meta: { requiresAuth: true, title: 'Pulse', permission: 'pulse.view' }
  },
  // ========== E-SIGNATURE MODULE (self-hosted document signing) ==========
  {
    path: '/esign',
    name: 'EsignList',
    component: () => import('@/modules/esign/views/EnvelopesListView.vue'),
    meta: { requiresAuth: true, title: 'E-Signature', permission: 'esign.envelope.view' }
  },
  {
    path: '/esign/new',
    name: 'EsignBuilder',
    component: () => import('@/modules/esign/views/DocumentBuilderView.vue'),
    meta: { requiresAuth: true, title: 'New Document', permission: 'esign.envelope.create' }
  },
  {
    path: '/esign/security',
    name: 'EsignSecurity',
    component: () => import('@/modules/esign/views/SecurityKeysView.vue'),
    meta: { requiresAuth: true, title: 'E-Signature Security', permission: 'esign.admin.keys' }
  },
  {
    path: '/esign/:id',
    name: 'EsignDetail',
    component: () => import('@/modules/esign/views/EnvelopeDetailView.vue'),
    meta: { requiresAuth: true, title: 'Document', permission: 'esign.envelope.view' }
  },
  // ========== PLANNING MODULE ==========
  {
    path: '/planning',
    name: 'Planning',
    component: () => import('@/modules/planning/PlanningView.vue'),
    meta: { requiresAuth: true, title: 'Planning', permission: 'planning.view' },
    children: [
      {
        path: '',
        redirect: '/planning/plans'
      },
      {
        path: 'create',
        name: 'CreatePlan',
        component: () => import('@/modules/planning/views/CreatePlanView.vue'),
        meta: { title: 'Create Plan', permission: 'planning.create' }
      },
      {
        path: 'plans',
        name: 'PlansList',
        component: () => import('@/modules/planning/views/PlansListView.vue'),
        meta: { title: 'Current Plans', permission: 'planning.view' }
      },
      {
        path: 'plans/:id',
        name: 'PlanDetail',
        component: () => import('@/modules/planning/views/PlanDetailView.vue'),
        meta: { title: 'Plan Detail', permission: 'planning.view' }
      },
      {
        path: 'resources',
        name: 'PlanningResources',
        component: () => import('@/modules/planning/views/ResourcesView.vue'),
        meta: { title: 'Resources', permission: 'planning.view' }
      }
    ]
  },
  // ========== IDEAS MODULE ==========
  {
    path: '/ideas',
    name: 'Ideas',
    component: () => import('@/views/IdeasView.vue'),
    // Ideas is now an admin-only area under Settings → Administration.
    meta: { requiresAuth: true, title: 'Ideas & Suggestions', permission: 'settings.administration' }
  },
  // ========== SUPPORT MODULE ==========
  {
    path: '/support',
    name: 'Support',
    component: () => import('@/modules/support/SupportView.vue'),
    meta: { requiresAuth: true, title: 'Support Center', permission: 'support.view' }
  },
  {
    // Deep link to a single ticket (opened from Telegram / global search).
    path: '/support/tickets/:id',
    name: 'SupportTicket',
    component: () => import('@/modules/support/SupportView.vue'),
    props: true,
    meta: { requiresAuth: true, title: 'Support Ticket', permission: 'support.view' }
  },
  // ========== KNOWLEDGE BASE MODULE ==========
  {
    path: '/knowledge',
    name: 'knowledge-base',
    component: () => import('@/views/KnowledgeBaseView.vue'),
    meta: { requiresAuth: true, title: 'Knowledge Base', permission: 'knowledge.view' }
  },
  {
    // Consolidated into the Learning Center (Proposals tab) — keep old links working.
    path: '/knowledge/moderation',
    name: 'knowledge-moderation',
    redirect: '/knowledge/learning'
  },
  {
    path: '/knowledge/learning',
    name: 'knowledge-learning',
    component: () => import('@/views/LearningCenterView.vue'),
    meta: { requiresAuth: true, title: 'Learning Center', permission: 'knowledge.moderation' }
  },
  {
    path: '/knowledge/training',
    name: 'knowledge-training',
    component: () => import('@/views/TrainingMaterialsView.vue'),
    meta: { requiresAuth: true, title: 'Training Materials', permission: 'knowledge.view' }
  },
  // ========== TASKS MODULE (Personal Tasks) ==========
  {
    path: '/tasks',
    name: 'Tasks',
    component: () => import('@/views/TasksView.vue'),
    meta: { requiresAuth: true, title: 'Tasks', permission: 'tasks.view' },
  },
  {
    // Tasks v2 — isolated new-design screen (Artem Figma "1.0 Tasks - all").
    // Purely additive; the /tasks screen above is left untouched.
    path: '/tasks-v2',
    name: 'TasksV2',
    component: () => import('@/views/TasksV2View.vue'),
    meta: { requiresAuth: true, title: 'Tasks v2', permission: 'tasks.view', hideModuleSubmenu: true },
  },
  // ========== DISPATCH MODULE (Real-time Operations) ==========
  {
    path: '/dispatch',
    name: 'Dispatch',
    component: () => import('@/views/dispatch/DispatchBoardView.vue'),
    meta: { requiresAuth: true, title: 'Dispatch Board', permission: 'dispatch.view' },
  },
  {
    path: '/dispatch/coverage-needs',
    name: 'CoverageNeeds',
    component: () => import('@/views/dispatch/CoverageNeedsView.vue'),
    meta: { requiresAuth: true, title: 'Coverage Needs', permission: 'dispatch.view' },
  },
  {
    // Live Board v2 — isolated new-design board (Artem Figma "1.0  Live board").
    // Purely additive; the /dispatch board above is untouched. Reuses the same
    // dispatch permission as /dispatch so access rules are identical.
    path: '/dispatch-v2',
    name: 'DispatchBoardV2',
    component: () => import('@/modules/dispatch/views/DispatchBoardV2View.vue'),
    meta: { requiresAuth: true, title: 'Live Board v2', permission: 'dispatch.view', hideModuleSubmenu: true },
  },
  // ========== FLEET (Crew Vehicles + Route/Trip planning — PLAN §4.4) ==========
  {
    path: '/fleet/vehicles',
    name: 'CrewVehicles',
    component: () => import('@/views/fleet/VehiclesView.vue'),
    meta: { requiresAuth: true, title: 'Crew Vehicles', permission: 'vehicles.view' },
  },
  {
    path: '/fleet/trips',
    name: 'TripPlanner',
    component: () => import('@/views/fleet/TripPlannerView.vue'),
    meta: { requiresAuth: true, title: 'Route Planner', permission: 'trips.view' },
  },
  // ========== PRODUCTION MODULE (merged Field Service + Territories) ==========
  {
    path: '/production',
    name: 'Production',
    component: () => import('@/modules/production/ProductionView.vue'),
    meta: { requiresAuth: true, title: 'Production', permission: 'territories.view' },
    children: [
      {
        path: '',
        redirect: () => workerListPath() // TC-67: cleaning -> /cleaners, else /technicians (see workerListPath)
      },
      {
        path: 'work-orders',
        redirect: '/jobs/board'
      },
      {
        path: 'schedule',
        redirect: '/jobs/schedule'
      },
      {
        // TC-67: cleaning companies want the worker list at /production/cleaners.
        // Add it as an alias so both URLs resolve to the same view — the old
        // /production/technicians path (deep links, bookmarks, voice map) keeps
        // working for every company; the menu points cleaning tenants at the
        // /cleaners alias.
        path: 'technicians',
        alias: 'cleaners',
        name: 'ProductionTechnicians',
        component: () => import('@/modules/territories/views/TechniciansView.vue'),
        meta: { title: 'Technicians', permission: 'territories.view_technicians' }
      },
      {
        path: 'technicians/form-settings',
        alias: 'cleaners/form-settings',
        name: 'ProductionTechnicianFormSettings',
        component: () => import('@/modules/territories/views/TechnicianFormSettingsView.vue'),
        meta: { title: 'Technician Form Settings', permission: 'territories.technician_settings.manage' }
      },
      {
        // Unified Cleaner Scorecard — reliability & quality KPIs for every
        // technician. MUST stay above 'technicians/:id' so 'scorecards' is not
        // captured as an :id. Alias /production/cleaners/scorecards for cleaning.
        path: 'technicians/scorecards',
        alias: 'cleaners/scorecards',
        name: 'ProductionCleanerScorecards',
        component: () => import('@/modules/territories/views/CleanerScorecardView.vue'),
        meta: { title: 'Cleaner Scorecard', permission: 'territories.view_technicians' }
      },
      {
        // TC-67: same view for the cleaner detail — /production/cleaners/:id is an
        // alias of the canonical /production/technicians/:id so both resolve here.
        path: 'technicians/:id',
        alias: 'cleaners/:id',
        name: 'ProductionTechnicianDetail',
        component: () => import('@/modules/territories/views/TechnicianDetailView.vue'),
        meta: { title: 'Technician Profile', permission: 'territories.view_technicians' }
      },
      {
        path: 'technicians/:id/scorecard',
        name: 'ProductionTechScorecard',
        component: () => import('@/modules/territories/views/TechScorecardView.vue'),
        meta: { title: 'Tech Scorecard', permission: 'territories.view_technicians' }
      },
      {
        path: 'technician-zones',
        name: 'ProductionTechnicianZones',
        component: () => import('@/modules/territories/views/TechnicianZonesView.vue'),
        meta: { title: 'Technician Zones', permission: 'territories.tz.view' }
      },
      {
        path: 'coverage-self-test',
        name: 'ProductionCoverageSelfTest',
        component: () => import('@/modules/territories/views/CoverageSelfTestView.vue'),
        meta: { title: 'Coverage self-test', permission: 'territories.tz.view' }
      },
      {
        path: 'projects',
        name: 'ProductionProjects',
        component: () => import('@/modules/territories/views/ProjectsView.vue'),
        meta: { title: 'Projects & Zones', permission: 'territories.view_projects' }
      },
      {
        path: 'projects/:id',
        name: 'ProductionProjectDetail',
        component: () => import('@/modules/territories/views/ProjectDetailView.vue'),
        meta: { title: 'Project Detail', permission: 'territories.view_projects' }
      },
      {
        path: 'regions',
        name: 'ProductionRegions',
        component: () => import('@/modules/territories/views/RegionsView.vue'),
        meta: { title: 'Regions', permission: 'territories.view_regions' }
      },
      {
        path: 'skills',
        name: 'ProductionSkills',
        component: () => import('@/modules/territories/views/SkillsView.vue'),
        meta: { title: 'Skills', permission: 'territories.view_skills' }
      },
      {
        path: 'analytics',
        name: 'ProductionAnalytics',
        component: () => import('@/modules/territories/views/AnalyticsView.vue'),
        meta: { title: 'Analytics', permission: 'territories.view_analytics' }
      },
      {
        path: 'tech-hours-report',
        name: 'TechHoursReport',
        component: () => import('@/modules/fieldservice/views/TechnicianHoursReport.vue'),
        meta: { title: 'Technician Hours', permission: 'accounting.view_payroll' }
      },
      {
        path: 'settings',
        name: 'ProductionSettings',
        component: () => import('@/modules/territories/views/SettingsView.vue'),
        meta: { title: 'Settings', permission: 'admin.manage_roles' }
      }
    ]
  },
  // Legacy redirects: /field-service -> /production
  {
    path: '/field-service',
    redirect: '/jobs/board'
  },
  {
    path: '/field-service/:pathMatch(.*)*',
    redirect: to => ({ path: `/production/${to.params.pathMatch}` })
  },
  // ========== CRM CUSTOMER CARD 360 (full-page route, before /crm to avoid conflict) ==========
  {
    path: '/crm/customers/:id/card',
    name: 'CustomerCard360',
    component: () => import('@/modules/crm/customer-card/CustomerCard360View.vue'),
    meta: { requiresAuth: true, title: 'Customer 360', permission: 'crm.view' }
  },
  {
    // Customer 360 v2 — isolated new-design detail page (Artem Figma
    // "1.0 Details - Overview"). Purely additive; the proven card route above
    // is untouched. Same :id param, so it's reachable at
    // /crm/customers/:id/card-v2. This is a DETAIL page, so it has no top-level
    // menu item; hideModuleSubmenu keeps the module submenu collapsed.
    path: '/crm/customers/:id/card-v2',
    name: 'CustomerCard360V2',
    component: () => import('@/modules/crm/customer-card/CustomerCard360V2View.vue'),
    meta: { requiresAuth: true, title: 'Customer v2', permission: 'crm.view', hideModuleSubmenu: true }
  },
  {
    // FSJ-6-00059 #20 — Full Contact Profile. The SAME new-design detail page
    // as Customer 360 v2, reused via profileType='contact', which hides the
    // Jobs / Finances / Appliances sections (a Contact has saved comms but no
    // job yet). The record is identical to the Customer record; once its first
    // job is saved the view redirects to /crm/customers/:id/card-v2 so there is
    // never a duplicate contact/customer. Same :id (CRM customer id), same
    // crm.view permission as the comparable Customer data.
    path: '/crm/contacts/:id/card-v2',
    name: 'ContactCard360V2',
    component: () => import('@/modules/crm/customer-card/CustomerCard360V2View.vue'),
    props: { profileType: 'contact' },
    meta: { requiresAuth: true, title: 'Contact', permission: 'crm.view', hideModuleSubmenu: true }
  },
  // ========== SALES / CSR PANEL (retired) ==========
  // The standalone Sales Panel was replaced by the CRM Panel. Keep the old
  // URL as a redirect so bookmarks and deep-links (?followup=, ?phone=,
  // ?call_log_id=, ?customer_id=) keep working.
  {
    path: '/crm/sales',
    redirect: to => ({ path: '/crm/panel', query: to.query })
  },
  {
    // Universal, config-driven CRM Panel built on the Sales Panel v2 engine
    // (role presets + omnichannel Inbound Stream dock).
    path: '/crm/panel',
    name: 'CrmPanel',
    component: () => import('@/views/crm/crm-panel/CrmPanelView.vue'),
    meta: { requiresAuth: true, title: 'CRM Panel', permission: 'crm.view' }
  },
  {
    // CRM 2 — isolated new-design panel (Artem). Purely additive; the existing
    // /crm/panel above is untouched.
    path: '/crm/panel2',
    name: 'CrmPanel2',
    component: () => import('@/views/crm/crm-panel-2/CrmPanel2View.vue'),
    meta: { requiresAuth: true, title: 'CRM 2', permission: 'crm.view' }
  },
  // ========== SALES SCRIPT (Sales Call Reproducible Insight & Performance Tracker) ==========
  {
    path: '/sales/activity',
    name: 'SalesActivityBoard',
    component: () => import('@/modules/sales/views/SalesActivityBoardView.vue'),
    meta: { requiresAuth: true, title: 'Sales Activity Board', permission: 'sales.view' }
  },
  {
    path: '/sales/team',
    name: 'SalesTeamDashboard',
    component: () => import('@/modules/sales/views/SalesTeamDashboardView.vue'),
    meta: { requiresAuth: true, title: 'Sales Team Dashboard', permission: 'sales.monitor' }
  },
  {
    // TV-1-00206: standalone Revenue Leakage report removed; KPIs live on Team Dashboard.
    path: '/sales/leakage',
    redirect: { name: 'SalesTeamDashboard' },
    meta: { requiresAuth: true, title: 'Sales Team Dashboard', permission: 'sales.monitor' }
  },
  {
    path: '/sales/manager/:userId',
    name: 'SalesManagerProfile',
    component: () => import('@/modules/sales/views/SalesManagerProfileView.vue'),
    meta: { requiresAuth: true, title: 'Sales Manager Profile', permission: 'sales.view' }
  },
  {
    path: '/sales/benchmarks',
    name: 'BenchmarkLibrary',
    component: () => import('@/modules/sales/views/BenchmarkLibraryView.vue'),
    meta: { requiresAuth: true, title: 'Benchmark Library', permission: 'benchmark.view' }
  },
  {
    path: '/sales/coaching',
    name: 'SalesCoaching',
    component: () => import('@/modules/sales/views/CoachingView.vue'),
    meta: { requiresAuth: true, title: 'Coaching', permission: 'coaching.view' }
  },
  {
    // Performance Coach v2 — isolated new-design screen (Artem Figma
    // "1.0 Performance Coach"). Purely additive; /sales/coaching above is
    // untouched.
    path: '/sales/coaching-v2',
    name: 'SalesCoachingV2',
    component: () => import('@/modules/sales/views/CoachingV2View.vue'),
    meta: { requiresAuth: true, title: 'Coaching v2', permission: 'coaching.view', hideModuleSubmenu: true }
  },
  {
    path: '/sales/my-homework',
    name: 'SalesMyHomework',
    component: () => import('@/modules/sales/views/MyHomeworkView.vue'),
    meta: { requiresAuth: true, title: 'My Homework', permission: 'sales.view' }
  },
  {
    path: '/sales/ai-coach/rules',
    name: 'SalesAICoachRules',
    component: () => import('@/modules/sales/views/SalesAICoachRulesView.vue'),
    meta: { requiresAuth: true, title: 'AI Coach Rules', permission: 'sales.monitor' }
  },
  // Sales rework C (2026-05-08): the v1 simulator was retired in favour of the
  // coach-driven v2 build. We keep the OLD route paths so external links,
  // bookmarks, AI-generated emails and old notifications keep working — they
  // just redirect to the v2 equivalent. The v1 component files
  // (SimulatorView.vue / SimulatorAttemptView.vue) are intentionally left on
  // disk for now, but no route loads them anymore.
  {
    path: '/sales/simulator',
    name: 'SalesSimulator',
    redirect: { name: 'SalesSimulatorV2' },
    meta: { requiresAuth: true, title: 'AI Simulator', permission: 'sales.view' }
  },
  {
    path: '/sales/coach-live',
    name: 'SalesLiveCoach',
    component: () => import('@/modules/sales/views/LiveCoachView.vue'),
    meta: { requiresAuth: true, title: 'Live AI Coach', permission: 'sales.view' }
  },
  {
    path: '/sales/simulator/:attemptId',
    name: 'SalesSimulatorAttempt',
    redirect: to => ({
      name: 'SalesSimulatorV2Attempt',
      params: { attemptId: to.params.attemptId },
    }),
    meta: { requiresAuth: true, title: 'AI Simulator — Attempt', permission: 'sales.view' }
  },
  {
    path: '/sales/training-analytics',
    name: 'SalesTrainingAnalytics',
    component: () => import('@/modules/sales/views/TrainingAnalyticsView.vue'),
    meta: { requiresAuth: true, title: 'Training Analytics', permission: 'sales.training.view_team' }
  },
  {
    // Training Analytics v2 — isolated new-design screen (Artem Figma
    // "1.0 Training Analytics"). Purely additive; the route above is untouched
    // and reuses the same team analytics API.
    path: '/sales/training-analytics-v2',
    name: 'TrainingAnalyticsV2',
    component: () => import('@/modules/sales/views/TrainingAnalyticsV2View.vue'),
    meta: {
      requiresAuth: true,
      title: 'Training Analytics v2',
      permission: 'sales.training.view_team',
      hideModuleSubmenu: true,
    }
  },
  {
    path: '/sales/training-analytics/:userId',
    name: 'SalesTrainingEmployeeAnalytics',
    component: () => import('@/modules/sales/views/TrainingEmployeeDetailView.vue'),
    meta: { requiresAuth: true, title: 'Training Analytics — Employee', permission: 'sales.training.view_team' }
  },
  {
    path: '/sales/training-analytics/:userId/attempt/:attemptId',
    name: 'SalesTrainingEmployeeAttempt',
    component: () => import('@/modules/sales/views/SimulatorV2AttemptView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Training Analytics — Attempt',
      permission: 'sales.training.view_team',
      managerTrainingView: true,
    }
  },
  {
    path: '/sales/simulator-v2',
    name: 'SalesSimulatorV2',
    component: () => import('@/modules/sales/views/SimulatorV2View.vue'),
    meta: { requiresAuth: true, title: 'AI Simulator', permission: 'sales.view' }
  },
  {
    path: '/sales/simulator-v2/:attemptId',
    name: 'SalesSimulatorV2Attempt',
    component: () => import('@/modules/sales/views/SimulatorV2AttemptView.vue'),
    meta: { requiresAuth: true, title: 'AI Simulator — Attempt', permission: 'sales.view' }
  },
  {
    path: '/crm/deals',
    name: 'CrmDeals',
    component: () => import('@/modules/sales/views/DealsListView.vue'),
    meta: { requiresAuth: true, title: 'Deals', permission: 'crm.view' }
  },
  {
    // Deals Kanban v2 — isolated new-design board (Artem Figma "1.0 Kanban").
    // Purely additive; the /crm/deals list + its kanban toggle are untouched.
    // Reuses salesApi.dealsList / dealsDict / dealUpdate, grouped by
    // deal_stage_id. hideModuleSubmenu keeps the new-design screen full-bleed.
    path: '/sales/deals-kanban-v2',
    name: 'DealsKanbanV2',
    component: () => import('@/modules/sales/views/DealsKanbanV2View.vue'),
    meta: { requiresAuth: true, title: 'Deals Kanban v2', permission: 'crm.view', hideModuleSubmenu: true }
  },
  {
    // Phase 5 CRM object: Companies (crm_companies, linked to crm_leads).
    path: '/crm/companies',
    name: 'CrmCompanies',
    component: () => import('@/modules/crm/views/CompaniesListView.vue'),
    meta: { requiresAuth: true, title: 'Companies', permission: 'crm.view' }
  },
  {
    path: '/crm/inbound',
    name: 'CrmInboundJournal',
    component: () => import('@/views/crm/InboundJournalView.vue'),
    meta: { requiresAuth: true, title: 'Inbound Journal', permission: 'crm.view' }
  },
  {
    path: '/crm/inbound-kanban',
    name: 'CrmInboundKanban',
    component: () => import('@/views/crm/InboundKanbanView.vue'),
    meta: { requiresAuth: true, title: 'Inbound Kanban', permission: 'crm.view' }
  },
  {
    path: '/crm/inbound-sales-report',
    name: 'CrmInboundSalesReport',
    component: () => import('@/views/crm/InboundSalesReportView.vue'),
    meta: { requiresAuth: true, title: 'Sales Report', permission: 'crm.view' }
  },
  {
    path: '/crm/lead-center',
    name: 'CrmLeadCenter',
    component: () => import('@/views/crm/InboundLeadCenterView.vue'),
    meta: { requiresAuth: true, title: 'Lead Center', permission: 'crm.view' }
  },
  {
    path: '/crm/mail-monitor',
    name: 'MailMonitor',
    component: () => import('@/views/communications/MailMonitorView.vue'),
    meta: { requiresAuth: true, title: 'Mail Monitor', permission: 'mail_monitor.view' }
  },
  {
    path: '/crm/deals/:dealId',
    name: 'CrmDealDetail',
    component: () => import('@/modules/sales/views/DealDetailView.vue'),
    meta: { requiresAuth: true, title: 'Deal', permission: 'crm.view' }
  },
  {
    // Commercial Site Assessment (Opportunity) — cleaning-audit §4.10
    path: '/sales/assessments',
    name: 'SalesAssessments',
    component: () => import('@/modules/sales/views/SiteAssessmentsListView.vue'),
    meta: { requiresAuth: true, title: 'Site Assessments', permission: 'sales.assessment.view' }
  },
  {
    path: '/sales/assessments/conversion',
    name: 'SalesAssessmentConversion',
    component: () => import('@/modules/sales/views/AssessmentConversionView.vue'),
    meta: { requiresAuth: true, title: 'Assessment Conversion Rate', permission: 'sales.assessment.report' }
  },
  {
    path: '/sales/assessments/:assessmentId',
    name: 'SalesAssessmentDetail',
    component: () => import('@/modules/sales/views/SiteAssessmentDetailView.vue'),
    meta: { requiresAuth: true, title: 'Site Assessment', permission: 'sales.assessment.view' }
  },
  {
    path: '/sales/settings',
    name: 'SalesSettings',
    component: () => import('@/modules/sales/views/SalesSettingsView.vue'),
    meta: { requiresAuth: true, title: 'Sales Settings', permission: 'sales.view' }
  },
  {
    path: '/settings/sales',
    redirect: { name: 'SalesSettings' }
  },
  // ========== BILLING (Atomic Usage-Based Billing) ==========
  {
    path: '/admin/billing',
    name: 'AdminBillingDashboard',
    component: () => import('@/modules/billing/views/AdminBillingDashboardView.vue'),
    meta: { requiresAuth: true, title: 'Billing Dashboard', permission: 'admin.billing.view' }
  },
  // ========== MERCHANT BILLING CABINET (AP-7-00459, client-facing) ==========
  {
    path: '/billing/cabinet',
    name: 'MerchantBillingCabinet',
    component: () => import('@/modules/billing/views/MerchantBillingCabinetView.vue'),
    meta: { requiresAuth: true, title: 'Billing', permission: 'billing.cabinet.view' }
  },
  // FSJ-6-00047: one invoice laid out like a telecom bill (+ PDF)
  {
    path: '/billing/invoices/:id(\\d+)',
    name: 'BillingInvoiceStatement',
    component: () => import('@/modules/billing/views/InvoiceStatementView.vue'),
    meta: { requiresAuth: true, title: 'Invoice', permission: 'billing.cabinet.view', statementScope: 'client' }
  },
  // FSJ-6-00047: choose a plan + add-on services, one cart, one payment
  {
    path: '/billing/plans',
    name: 'BillingChoosePlan',
    component: () => import('@/modules/billing/views/ChoosePlanView.vue'),
    meta: { requiresAuth: true, title: 'Choose your plan', permission: 'billing.cabinet.view' }
  },
  {
    path: '/billing/services',
    name: 'BillingServices',
    component: () => import('@/modules/billing/views/BillingServicesView.vue'),
    meta: { requiresAuth: true, title: 'Services', permission: 'billing.cabinet.view' }
  },
  // ========== PAYMENTS (Stripe Connect — tenant payment acceptance, Flow B) ==========
  {
    path: '/payments',
    name: 'PaymentsList',
    component: () => import('@/modules/payments/PaymentsListView.vue'),
    meta: { requiresAuth: true, title: 'Payments', permission: 'payments.view' }
  },
  {
    path: '/payments/collect',
    name: 'PaymentsCollect',
    component: () => import('@/modules/payments/CollectPaymentView.vue'),
    meta: { requiresAuth: true, title: 'Collect Payment', permission: 'payments.collect' }
  },
  {
    path: '/payments/setup',
    name: 'PaymentsSetup',
    component: () => import('@/modules/payments/PaymentsSetupView.vue'),
    meta: { requiresAuth: true, title: 'Payments Setup', permission: 'payments.settings' }
  },
  // ========== EMAIL MARKETING (before CRM to avoid route conflict) ==========
  {
    path: '/crm/email-marketing',
    name: 'EmailMarketing',
    component: () => import('@/views/crm/email-marketing/EmailMarketingView.vue'),
    meta: { requiresAuth: true, title: 'Email Marketing', permission: 'crm.view_email_marketing' }
  },
  {
    path: '/crm/seo-drafts',
    name: 'SeoCaseDrafts',
    component: () => import('@/modules/marketing/seo/SeoCaseDraftsView.vue'),
    meta: { requiresAuth: true, title: 'Case Study SEO Drafts', permission: 'marketing.seo.drafts' }
  },
  {
    path: '/crm/loyalty-referrals',
    name: 'LoyaltyReferralsSetup',
    component: () => import('@/modules/marketing/loyalty/LoyaltySetupView.vue'),
    meta: {
      requiresAuth: true,
      title: 'Loyalty & Referrals Setup',
      permission: ['marketing.loyalty.view', 'marketing.loyalty.manage'],
    }
  },
  // ========== CRM ANALYTICS (before CRM to avoid route conflict) ==========
  {
    path: '/crm/analytics',
    name: 'CRMAnalytics',
    component: () => import('@/views/crm/CRMAnalyticsView.vue'),
    meta: { requiresAuth: true, title: 'CRM Analytics', permission: 'crm.view_analytics' }
  },
  {
    path: '/crm/customer-export',
    name: 'CrmCustomerExport',
    component: () => import('@/views/crm/CustomerExportView.vue'),
    meta: { requiresAuth: true, title: 'Customer Export', permission: 'crm.customer_export' }
  },
  // Cross-Channel Attribution Dashboard (TZ: Дашборд сквозной аналитики)
  {
    path: '/crm/channel-attribution',
    name: 'CRMChannelAttribution',
    component: () => import('@/views/crm/ChannelAttributionView.vue'),
    meta: { requiresAuth: true, title: 'Cross-Channel Attribution', permission: 'crm.view_analytics' }
  },
  {
    path: '/crm/marketing-sheets',
    name: 'CRMMarketingSheets',
    component: () => import('@/views/crm/MarketingSheetDashboard.vue'),
    meta: { requiresAuth: true, title: 'Marketing Sheets', permission: 'crm.marketing_sheets.view' }
  },
  {
    path: '/crm/google-ads',
    name: 'CRMGoogleAds',
    component: () => import('@/views/crm/GoogleAdsAnalyticsView.vue'),
    meta: { requiresAuth: true, title: 'Google Ads / LSA', permission: 'crm.view_analytics' }
  },
  {
    path: '/crm/ad-costs',
    name: 'CRMAdCosts',
    component: () => import('@/views/crm/AdCostsView.vue'),
    meta: { requiresAuth: true, title: 'Ad Costs / Marketing Spend', permission: 'crm.view_analytics' }
  },
  {
    path: '/crm/portal-analytics',
    name: 'PortalAnalytics',
    component: () => import('@/views/crm/PortalAnalyticsView.vue'),
    meta: { requiresAuth: true, title: 'Portal Analytics', permission: 'customer_portal.view' }
  },
  // Call Tracking (DNI) — собственная замена CallRail (динамическая подмена номеров)
  {
    path: '/crm/call-tracking',
    name: 'CallTracking',
    component: () => import('@/views/crm/CallTrackingView.vue'),
    meta: { requiresAuth: true, title: 'Call Tracking (DNI)', permission: 'tracking.view' }
  },
  // ========== REPUTATION (before CRM to avoid route conflict) ==========
  {
    path: '/crm/automations',
    name: 'CrmCustomerAutomations',
    component: () => import('@/modules/crm/views/automations/CustomerAutomationsView.vue'),
    meta: { requiresAuth: true, title: 'Customer Automations', permission: 'crm.automations.view' }
  },
  {
    path: '/crm/reputation/reviews',
    name: 'CrmReputationReviews',
    component: () => import('@/modules/crm/views/reputation/ReviewsInboxView.vue'),
    meta: { requiresAuth: true, title: 'Reviews Inbox', permission: 'crm.reputation.view' }
  },
  {
    path: '/crm/reputation/recovery',
    name: 'CrmReputationRecovery',
    component: () => import('@/modules/crm/views/reputation/RecoveryView.vue'),
    meta: { requiresAuth: true, title: 'Recovery', permission: 'crm.reputation.view' }
  },
  {
    path: '/crm/reputation/followup-monitor',
    name: 'CrmReputationFollowupMonitor',
    component: () => import('@/modules/crm/views/reputation/ReviewFollowupMonitorView.vue'),
    meta: { requiresAuth: true, title: 'Review Follow-up Monitor', permission: 'crm.reputation.view' }
  },
  {
    path: '/crm/reputation/team-quality',
    name: 'CrmReputationTeamQuality',
    component: () => import('@/modules/crm/views/reputation/TeamQualityView.vue'),
    meta: { requiresAuth: true, title: 'Team Quality', permission: 'crm.reputation.view' }
  },
  {
    path: '/crm/reputation/surveys',
    name: 'CrmReputationSurveys',
    component: () => import('@/modules/crm/views/reputation/SurveysView.vue'),
    meta: { requiresAuth: true, title: 'NPS / CSAT', permission: 'crm.reputation.view' }
  },
  {
    path: '/crm/reputation/analytics',
    name: 'CrmReputationAnalytics',
    component: () => import('@/modules/crm/views/reputation/ReputationAnalyticsView.vue'),
    meta: { requiresAuth: true, title: 'Reputation Analytics', permission: 'crm.reputation.view' }
  },
  {
    path: '/crm/reputation/campaigns',
    name: 'CrmReputationCampaigns',
    component: () => import('@/modules/crm/views/reputation/CampaignBuilderView.vue'),
    meta: { requiresAuth: true, title: 'Review Campaigns', permission: 'crm.reputation.manage' }
  },
  {
    path: '/crm/reputation/settings',
    name: 'CrmReputationSettings',
    component: () => import('@/modules/crm/views/reputation/ReputationSettingsView.vue'),
    meta: { requiresAuth: true, title: 'Reputation Settings', permission: 'crm.reputation.view' }
  },
  {
    path: '/crm/reputation/competitors',
    name: 'CrmReputationCompetitors',
    component: () => import('@/modules/crm/views/reputation/CompetitorBenchmarkView.vue'),
    meta: { requiresAuth: true, title: 'Competitor Benchmark', permission: 'crm.reputation.view' }
  },
  {
    path: '/crm/reputation/widget',
    name: 'CrmReputationWidget',
    component: () => import('@/modules/crm/views/reputation/ReviewWidgetView.vue'),
    meta: { requiresAuth: true, title: 'Review Widget & QR', permission: 'crm.reputation.view' }
  },
  {
    path: '/crm/reputation/digest',
    name: 'CrmReputationDigest',
    component: () => import('@/modules/crm/views/reputation/WeeklyDigestView.vue'),
    meta: { requiresAuth: true, title: 'Weekly Digest', permission: 'crm.reputation.view' }
  },
  {
    // Customers v2 — isolated NEW-DESIGN screen (Artem Figma "1.0 Customers").
    // Purely additive; the classic /crm/customers list (CRMView) is untouched.
    // Standalone top-level route (the /crm parent is a tabs container with no
    // nested <router-view>), so this full-page view renders in the app shell.
    path: '/crm/customers-v2',
    name: 'CustomersV2',
    component: () => import('@/views/CRMCustomersV2View.vue'),
    meta: { requiresAuth: true, title: 'Customers v2', permission: 'crm.view', hideModuleSubmenu: true }
  },
  // ========== CRM MODULE ==========
  {
    path: '/crm',
    name: 'CRM',
    component: () => import('@/views/CRMView.vue'),
    meta: { requiresAuth: true, title: 'CRM', permission: 'crm.view' },
    children: [
      {
        path: '',
        redirect: '/crm/leads'
      },
      {
        path: 'leads',
        name: 'Leads',
        component: () => import('@/views/CRMView.vue'),
        meta: { title: 'Lead Sources', permission: 'crm.view_leads' }
      },
      {
        path: 'leads/sources',
        name: 'LeadSources',
        component: () => import('@/views/CRMView.vue'),
        meta: { title: 'Lead Sources', permission: 'crm.manage_sources' }
      },
      {
        path: 'leads/categories',
        name: 'LeadCategories',
        component: () => import('@/views/CRMView.vue'),
        meta: { title: 'Source Categories', permission: 'crm.manage_sources' }
      },
      {
        path: 'leads/flows',
        name: 'LeadFlows',
        component: () => import('@/views/CRMView.vue'),
        meta: { title: 'Lead Flows', permission: 'crm.manage_sources' }
      },
      {
        path: 'customers',
        name: 'Customers',
        component: () => import('@/views/CRMView.vue'),
        meta: { title: 'Customers' }
      },
      {
        // TICK-00019: this path used to mount CRMView (the customer LIST), which
        // ignores :id, so any deep link to /crm/customers/<id> (voice assistant,
        // task drawer, sales context, a pasted URL) just showed the general list
        // instead of the profile. Redirect to the real full-page customer card.
        path: 'customers/:id',
        name: 'CustomerDetail',
        redirect: to => ({ name: 'CustomerCard360V2', params: { id: to.params.id } })
      },
      {
        // TICK-00149: "Contacts" showed the identical Customers list (same
        // CRMView) — a duplicate. Redirect to the single canonical Customers
        // page so old bookmarks / voice links keep working.
        path: 'contacts',
        name: 'Contacts',
        redirect: '/crm/customers'
      },
      {
        // TICK-00149: "History" pointed at a route that never existed (empty,
        // dead-end tab). Per-domain interaction history lives in the Inbound
        // Journal; per-customer history is on the Customer 360 card.
        path: 'history',
        name: 'CrmHistoryRedirect',
        redirect: '/crm/inbound'
      },
      
    ]
  },
  // ========== OPERATIONS MODULE (Parts Management) ==========
  {
    path: '/operations',
    name: 'Operations',
    component: () => import('@/modules/operations/OperationsView.vue'),
    meta: { requiresAuth: true, title: 'Operations', permission: 'operations.view' },
    children: [
      {
        path: '',
        redirect: '/operations/parts-requests'
      },
      {
        path: 'parts-manager',
        name: 'PartsManagerDashboard',
        component: () => import('@/modules/operations/views/PartsManagerDashboardView.vue'),
        meta: { title: 'Parts Manager Dashboard', permission: 'parts.manage_dashboard' }
      },
      {
        path: 'purchase-orders',
        name: 'PurchaseOrders',
        component: () => import('@/modules/operations/views/PurchaseOrdersView.vue'),
        meta: { title: 'Purchase Orders', permission: 'parts.manage_po' }
      },
      {
        // Live Reliable + Marcone supplier catalog search.
        path: 'test-parts-order',
        name: 'TestPartsOrder',
        component: () => import('@/modules/operations/views/TestPartsOrderView.vue'),
        meta: { title: 'Test Parts Order', permission: 'parts.manage_po' }
      },
      {
        path: 'mobile-receive',
        name: 'MobilePartsReceive',
        component: () => import('@/modules/operations/views/MobilePartsReceiveView.vue'),
        meta: { title: 'Mobile Receive', permission: 'parts.process_request' }
      },
      {
        path: 'van-audits',
        name: 'VanAudits',
        component: () => import('@/modules/operations/views/VanAuditsView.vue'),
        meta: { title: 'Van Audits', permission: 'parts.van_audit' }
      },
      {
        path: 'returns',
        name: 'PartsReturns',
        component: () => import('@/modules/operations/views/PartsReturnsView.vue'),
        meta: { title: 'Returns / RMA', permission: 'parts.manage_returns' }
      },
      {
        path: 'supplier-invoices',
        name: 'SupplierInvoices',
        component: () => import('@/modules/operations/views/SupplierInvoicesView.vue'),
        meta: { title: 'Supplier Invoices', permission: 'parts.view_supplier_invoices' }
      },
      {
        path: 'supplier-invoices/:id',
        name: 'SupplierInvoiceDetail',
        component: () => import('@/modules/operations/views/SupplierInvoiceDetailView.vue'),
        meta: { title: 'Invoice Reconciliation', permission: 'parts.view_supplier_invoices' }
      },
      {
        path: 'payment-proposals',
        name: 'PaymentProposals',
        component: () => import('@/modules/operations/views/PaymentProposalsView.vue'),
        meta: { title: 'Payment Proposals', permission: 'parts.view_supplier_invoices' }
      },
      {
        path: 'supplier-inbox',
        name: 'SupplierInbox',
        component: () => import('@/modules/operations/views/SupplierInboxView.vue'),
        meta: { title: 'Supplier Inbox', permission: 'parts.manage_po' }
      },
      {
        path: 'analytics',
        name: 'PartsAnalytics',
        component: () => import('@/modules/operations/views/PartsAnalyticsView.vue'),
        meta: { title: 'Parts Analytics', permission: 'parts.view_inventory' }
      },
      {
        path: 'audit-log',
        name: 'PartsAuditLog',
        component: () => import('@/modules/operations/views/PartsAuditLogView.vue'),
        meta: { title: 'Parts Audit Log', permission: 'parts.view_audit' }
      },
      {
        path: 'parts-audit',
        name: 'PartsAudit',
        component: () => import('@/modules/operations/views/PartsAuditJournalView.vue'),
        meta: { title: 'Parts Audit Journal', permission: 'parts.audit_view' }
      },
      {
        path: 'inventory-search',
        name: 'InventorySearch',
        component: () => import('@/modules/operations/views/InventorySearchView.vue'),
        meta: { title: 'Inventory Search', permission: 'parts.view_inventory' }
      },
      {
        path: 'parts-requests',
        name: 'PartsRequests',
        component: () => import('@/modules/operations/views/PartsRequestsView.vue'),
        meta: { title: 'Parts Requests', permission: 'parts.view_requests' }
      },
      {
        path: 'parts-requests/:id',
        name: 'PartsRequestDetail',
        component: () => import('@/modules/operations/views/PartsRequestsView.vue'),
        meta: { title: 'Request Detail', permission: 'parts.view_requests' }
      },
      {
        path: 'catalog',
        name: 'PartsCatalog',
        component: () => import('@/modules/operations/views/PartsCatalogView.vue'),
        meta: { title: 'Parts Catalog', permission: 'parts.view_catalog' }
      },
      {
        path: 'quotes',
        name: 'Quotes',
        component: () => import('@/modules/operations/views/QuotesView.vue'),
        meta: { title: 'Quotes', permission: 'parts.view_quotes' }
      },
      {
        path: 'quotes/builder',
        name: 'QuoteBuilder',
        component: () => import('@/modules/operations/views/QuotesView.vue'),
        meta: { title: 'Quote Builder', permission: 'parts.create_quote' }
      },
      {
        path: 'quotes/:id',
        name: 'QuoteDetail',
        component: () => import('@/modules/operations/views/QuotesView.vue'),
        meta: { title: 'Quote Detail', permission: 'parts.view_quotes' }
      },
      {
        path: 'inventory',
        name: 'Inventory',
        component: () => import('@/modules/operations/views/InventoryView.vue'),
        meta: { title: 'Inventory', permission: 'parts.view_inventory' }
      },
      {
        path: 'supplies',
        name: 'Supplies',
        component: () => import('@/modules/operations/views/SuppliesView.vue'),
        meta: { title: 'Supplies', permission: 'supplies.view' }
      },
      {
        path: 'inventory/bulk-import',
        name: 'InventoryBulkImport',
        component: () => import('@/modules/operations/views/BulkImportView.vue'),
        meta: { title: 'Bulk Import Inventory', permission: 'parts.bulk_import' }
      },
      {
        path: 'inventory/audits',
        name: 'InventoryAudits',
        component: () => import('@/modules/operations/views/StockTakesView.vue'),
        meta: { title: 'Inventory Audits', permission: 'parts.stock_take.view' }
      },
      {
        path: 'inventory/audits/:id',
        name: 'InventoryAuditDetail',
        component: () => import('@/modules/operations/views/StockTakeDetailView.vue'),
        meta: { title: 'Audit', permission: 'parts.stock_take.view' }
      },
      {
        // v6.0 Phase 6 - OCR/barcode telemetry dashboard. Restricted to
        // admins, owner, parts manager, ops manager, god mode (see the
        // 2026_05_21_parts_recognition_log.sql migration).
        path: 'parts/recognition-stats',
        name: 'PartsRecognitionStats',
        component: () => import('@/modules/operations/views/PartsRecognitionStatsView.vue'),
        meta: {
          title: 'Label Recognition Stats',
          permission: 'parts.view_recognition_log',
        },
      },
      {
        // v6.2 Phase 1 — supplier-document scan sandbox.
        // Drag & drop a photo, see vendor template + parsed header / lines /
        // job suggestions JSON in real time. No DB writes happen.
        path: 'parts/document-scan-test',
        name: 'PartsDocumentScanTest',
        component: () => import('@/modules/operations/views/PartsDocumentScanTestView.vue'),
        meta: {
          title: 'Document Scan (Sandbox)',
          permission: 'parts.process_request',
        },
      },
      {
        path: 'suppliers',
        name: 'Suppliers',
        component: () => import('@/modules/operations/views/SuppliersView.vue'),
        meta: { title: 'Suppliers', permission: 'parts.view_suppliers' }
      },
      {
        path: 'vehicle-stock',
        name: 'VehicleStock',
        component: () => import('@/modules/operations/views/VehicleStockView.vue'),
        meta: { title: 'Vehicle Stock', permission: 'parts.view_inventory' }
      },
      {
        // Canonical detail key is warehouseId (VEHICLE warehouse). Legacy
        // bookmarks that pass technicians.id still resolve server-side.
        path: 'vehicle-stock/:warehouseId',
        name: 'VehicleStockDetail',
        component: () => import('@/modules/operations/views/VehicleStockDetailView.vue'),
        meta: { title: 'Vehicle Stock Detail', permission: 'parts.view_inventory' }
      },
      {
        // FSJ-6-00070: legacy Truck Stock menu → Vehicle Stock hub (Replenishment tab)
        path: 'truck-stock',
        name: 'TruckStockManager',
        redirect: { path: '/operations/vehicle-stock', query: { tab: 'replenishment' } },
        meta: { title: 'Truck Stock', permission: 'parts.truck_stock.manage' }
      },
      {
        path: 'warehouses',
        name: 'OperationsWarehouses',
        component: () => import('@/modules/assets/views/WarehousesView.vue'),
        meta: { title: 'Warehouses', permission: 'parts.view_inventory' }
      }
    ]
  },
  // ========== ACCOUNTING MODULE ==========
  {
    path: '/accounting',
    name: 'Accounting',
    component: () => import('@/modules/accounting/AccountingView.vue'),
    meta: { requiresAuth: true, title: 'Accounting', permission: 'accounting.view' },
    children: [
      {
        path: 'reports/technician-payroll',
        name: 'TechnicianPayroll',
        component: () => import('@/modules/accounting/views/TechnicianPayrollReport.vue'),
        meta: { title: 'Payroll Report', permission: 'accounting.view_payroll' }
      },
      {
        path: 'reports/back-office-payroll',
        name: 'BackOfficePayroll',
        component: () => import('@/modules/accounting/views/BackOfficePayrollReport.vue'),
        meta: { title: 'Back Office Payroll', permission: 'accounting.view_payroll' }
      },
      {
        path: 'settings/region-payroll-rates',
        name: 'RegionPayrollRates',
        component: () => import('@/modules/accounting/views/RegionPayrollRatesView.vue'),
        meta: { title: 'Region Payroll Rates', permission: 'accounting.view_payroll' }
      },
      {
        path: 'holiday-calendar',
        name: 'HolidayCalendar',
        component: () => import('@/modules/accounting/views/HolidayCalendarView.vue'),
        meta: { title: 'Holiday Calendar', permission: ['accounting.manage_holidays', 'hr.employees'] }
      },
      {
        path: 'expenses',
        name: 'TechnicianExpenses',
        component: () => import('@/modules/accounting/views/TechnicianExpensesView.vue'),
        meta: { title: 'Technician Expenses', permission: 'accounting.view' }
      },
      {
        path: 'invoices',
        name: 'Invoices',
        component: () => import('@/modules/accounting/views/InvoicesView.vue'),
        meta: { title: 'Invoices', permission: 'finance.manage_invoices' }
      },
      {
        path: 'payments',
        name: 'Payments',
        component: () => import('@/modules/accounting/views/PaymentsView.vue'),
        meta: { title: 'Payments', permission: 'finance.manage_payments' }
      },
      {
        path: 'tips',
        name: 'TipsReport',
        component: () => import('@/modules/accounting/views/TipsReportView.vue'),
        meta: { title: 'Tips', permission: 'finance.manage_payments' }
      },
      {
        path: 'batching',
        name: 'Batching',
        component: () => import('@/modules/accounting/views/BatchingView.vue'),
        meta: { title: 'Batching & Export', permission: 'finance.manage_batches' }
      },
      {
        path: 'dashboard',
        name: 'FinanceDashboard',
        component: () => import('@/modules/accounting/views/FinanceDashboard.vue'),
        meta: { title: 'Finance Dashboard', permission: 'finance.view_dashboard' }
      },
      {
        path: 'reports',
        name: 'FinanceReports',
        component: () => import('@/modules/accounting/views/ReportsView.vue'),
        meta: { title: 'Financial Reports', permission: 'finance.view_reports' }
      },
      {
        path: 'profit-loss',
        name: 'ProfitLossReport',
        component: () => import('@/modules/accounting/views/ProfitLossView.vue'),
        meta: { title: 'Profit & Loss', permission: 'finance.view_reports' }
      },
      {
        path: 'profit-loss/settings',
        name: 'ProfitLossSettings',
        component: () => import('@/modules/accounting/views/PnlSettingsView.vue'),
        meta: { title: 'P&L Data Sources', permission: 'finance.view_reports' }
      },
      {
        path: 'admin-entry',
        name: 'AdminManualEntry',
        component: () => import('@/modules/accounting/views/AdminManualEntryView.vue'),
        meta: { title: 'Admin / Manual Entry', permission: 'finance.view_reports' }
      },
      {
        path: 'reimbursements',
        name: 'ReimbursementJournal',
        component: () => import('@/modules/accounting/views/ReimbursementJournalView.vue'),
        meta: { title: 'Reimbursement Journal', permission: 'accounting.view' }
      },
      {
        path: 'ar-management',
        name: 'ARManagement',
        component: () => import('@/modules/accounting/views/ARManagementView.vue'),
        meta: { title: 'AR Management', permission: 'finance.manage_ar' }
      },
      {
        path: 'collections',
        name: 'CollectionsQueue',
        component: () => import('@/modules/accounting/views/CollectionsQueueView.vue'),
        meta: { title: 'Collections', permission: 'collections.view' }
      },
      {
        path: 'settings',
        name: 'FinanceSettings',
        component: () => import('@/modules/accounting/views/FinanceSettingsView.vue'),
        meta: { title: 'Finance Settings', permission: 'finance.manage_settings' }
      },
      {
        path: 'profitability',
        name: 'ProfitabilityDashboard',
        component: () => import('@/modules/jobs/views/FinancialDashboardView.vue'),
        meta: { title: 'Profitability & Targets', permission: 'accounting.view' }
      },
      {
        path: 'payment-control',
        name: 'PaymentControl',
        component: () => import('@/modules/accounting/views/PaymentControlView.vue'),
        meta: { title: 'Payment Control', permission: 'accounting.view' }
      },
      {
        path: 'payment-reconciliation',
        name: 'PaymentReconciliation',
        component: () => import('@/modules/accounting/views/PaymentReconciliationView.vue'),
        meta: { title: 'Payment Reconciliation', permission: 'finance.reconcile' }
      }
    ]
  },
  // ========== REPORTS MODULE ==========
  {
    path: '/reports',
    name: 'Reports',
    component: () => import('@/views/ReportsView.vue'),
    meta: { requiresAuth: true, title: 'Reports', permission: 'reports.view' },
    children: [
      {
        path: '',
        name: 'ReportsDashboard',
        component: () => import('@/views/ReportsView.vue'),
        meta: { title: 'Dashboards' }
      },
      {
        path: 'analytics',
        name: 'Analytics',
        component: () => import('@/views/ReportsView.vue'),
        meta: { title: 'Metrics', permission: 'reports.view' }
      },
      {
        path: 'builder',
        name: 'DashboardBuilder',
        component: () => import('@/views/ReportsView.vue'),
        meta: { title: 'Dashboard Builder', permission: 'reports.create' }
      },
      {
        path: 'builder/:id',
        name: 'DashboardBuilderEdit',
        component: () => import('@/views/ReportsView.vue'),
        meta: { title: 'Edit Dashboard', permission: 'reports.edit' }
      },
      {
        path: 'export',
        name: 'ExportReports',
        component: () => import('@/views/ReportsView.vue'),
        meta: { title: 'Export', permission: 'reports.export' }
      }
    ]
  },
  // ========== CALL REPORTS (CallRail) ==========
  {
    path: '/reports/calls',
    name: 'CallReports',
    component: () => import('@/views/CallReportsView.vue'),
    meta: { requiresAuth: true, title: 'Call Reports', permission: 'reports.calls' }
  },
  // ========== PARTS COST REPORT (standalone page) ==========
  {
    path: '/reports/parts-cost',
    name: 'PartsCostReport',
    component: () => import('@/views/reports/PartsCostReportView.vue'),
    meta: { requiresAuth: true, title: 'Parts Cost Report', permission: 'reports.view' }
  },
  {
    path: '/reports/parts-orders',
    name: 'PartsOrdersReport',
    component: () => import('@/views/reports/PartsOrdersReportView.vue'),
    meta: { requiresAuth: true, title: 'Parts Orders Report', permission: 'reports.view' }
  },
  // ========== JOBS BY TAG REPORT (standalone page) ==========
  {
    path: '/reports/jobs-by-tag',
    name: 'JobsByTagReport',
    component: () => import('@/views/reports/JobsByTagReportView.vue'),
    meta: { requiresAuth: true, title: 'Jobs by Tag', permission: 'reports.view' }
  },
  // ========== CANCELLATIONS REPORT (AP-7-00452) ==========
  {
    path: '/reports/cancellations',
    name: 'CancellationsReport',
    component: () => import('@/views/reports/CancellationsReportView.vue'),
    meta: { requiresAuth: true, title: 'Cancellations', permission: 'reports.view' }
  },
  // ========== OPERATIONS REPORT (FSJ-6-00014) ==========
  {
    path: '/reports/operations',
    name: 'OperationsReport',
    component: () => import('@/views/reports/OperationsReportView.vue'),
    meta: { requiresAuth: true, title: 'Operations', permission: 'reports.view' }
  },
  // ========== MISSED CALLS REPORT (standalone page) ==========
  {
    path: '/reports/missed-calls',
    name: 'MissedCallsReport',
    component: () => import('@/views/reports/MissedCallsReportView.vue'),
    meta: { requiresAuth: true, title: 'Missed Calls', permission: 'dialer.view_reports' }
  },
  // ========== PHONE NUMBERS REPORT (standalone page) ==========
  {
    path: '/reports/phone-numbers',
    name: 'PhoneNumbersReport',
    component: () => import('@/views/reports/PhoneNumbersReportView.vue'),
    meta: { requiresAuth: true, title: 'Phone Numbers', permission: 'reports.phone_numbers' }
  },
  // ========== DATA FLOW MODULE ==========
  {
    path: '/dataflow',
    name: 'DataFlow',
    component: () => import('@/modules/dataflow/DataFlowView.vue'),
    meta: { requiresAuth: true, title: 'Data Flow', permission: 'reports.view' }
  },
  // ========== FIELD SERVICE (TECHNICIAN) MODULE ==========
  {
    path: '/technician',
    name: 'TechnicianApp',
    component: () => import('@/modules/fieldservice/technician/TechnicianApp.vue'),
    meta: { requiresAuth: true, title: 'Technician', isTechnicianRoute: true },
    children: [
      {
        path: '',
        name: 'TechDashboard',
        component: () => import('@/modules/fieldservice/technician/TechDashboard.vue'),
        meta: { title: 'Dashboard' }
      },
      {
        path: 'jobs',
        name: 'TechJobs',
        component: () => import('@/modules/fieldservice/technician/TechJobsList.vue'),
        meta: { title: 'My Jobs' }
      },
      {
        // Must stay above 'jobs/:id', otherwise "new" is read as a job id.
        // Housecall Pro skin only; the other presets send the technician to
        // the office New job page.
        path: 'jobs/new',
        name: 'TechNewJob',
        component: () => import('@/modules/fieldservice/technician/hcp/HcpNewJob.vue'),
        // Housecall Pro books a job in a modal that covers the phone; chromeless
        // hides the shell header and tab bar so ours does the same.
        meta: { title: 'New Job', chromeless: true }
      },
      {
        path: 'jobs/:id',
        name: 'TechJobDetail',
        component: () => import('@/modules/fieldservice/technician/TechJobDetail.vue'),
        meta: { title: 'Job Details' }
      },
      // Cleaning preset (cleaning_v1) screens. In the v2 shell these render via
      // the config-driven ScreenRenderer (matched by route_path); the component
      // here is only a graceful fallback for a non-v2 mount of the path.
      {
        path: 'pay',
        name: 'TechPay',
        component: () => import('@/modules/fieldservice/technician/TechProfile.vue'),
        meta: { title: 'My Pay' }
      },
      {
        path: 'jobs/:id/checkin',
        name: 'TechJobCheckin',
        component: () => import('@/modules/fieldservice/technician/TechJobDetail.vue'),
        meta: { title: 'Check-in' }
      },
      {
        path: 'jobs/:id/lockout',
        name: 'TechJobLockout',
        component: () => import('@/modules/fieldservice/technician/TechJobDetail.vue'),
        meta: { title: 'Lockout' }
      },
      {
        path: 'jobs/:id/checkout',
        name: 'TechJobCheckout',
        component: () => import('@/modules/fieldservice/technician/TechJobDetail.vue'),
        meta: { title: 'Check-out' }
      },
      {
        path: 'jobs/:id/zones/:zoneId',
        name: 'TechJobZone',
        component: () => import('@/modules/fieldservice/technician/TechJobDetail.vue'),
        meta: { title: 'Zone' }
      },
      {
        path: 'map',
        name: 'TechMap',
        component: () => import('@/modules/fieldservice/technician/TechMap.vue'),
        meta: { title: 'Map' }
      },
      {
        path: 'profile',
        name: 'TechProfile',
        component: () => import('@/modules/fieldservice/technician/TechProfile.vue'),
        meta: { title: 'Profile' }
      },
      {
        path: 'chat',
        name: 'TechChat',
        component: () => import('@/modules/fieldservice/technician/TechChat.vue'),
        meta: { title: 'Messages' }
      },
      {
        // HCP Interface "Customers" tab. Rendered by the config-driven v2 shell
        // (ScreenRenderer -> hcp_customers widget); this route only needs to
        // exist so router.push('/technician/customers') matches and the shell
        // resolves the screen. The component is a graceful fallback for any
        // non-v2 mount of this path.
        path: 'customers',
        name: 'TechCustomers',
        component: () => import('@/modules/fieldservice/technician/hcp/HcpCustomers.vue'),
        meta: { title: 'Customers' }
      },
      {
        // HCP Interface "My Earnings & Reviews" tab (TICK-00035). Rendered by
        // the config-driven v2 shell (ScreenRenderer -> hcp_earnings widget);
        // this route only needs to exist so router.push('/technician/earnings')
        // matches and the shell resolves the screen. The component is a
        // graceful fallback for any non-v2 mount of this path.
        path: 'earnings',
        name: 'TechEarnings',
        component: () => import('@/modules/fieldservice/technician/hcp/HcpEarningsReviews.vue'),
        meta: { title: 'My Earnings & Reviews' }
      },
      {
        // Jobber Original "Timesheet" tab. Rendered by the config-driven v2
        // shell (ScreenRenderer -> jobber_timesheet widget); this route only
        // needs to exist so router.push('/technician/timesheet') matches. The
        // component is a graceful fallback for any non-v2 mount of this path.
        path: 'timesheet',
        name: 'TechTimesheet',
        component: () => import('@/modules/fieldservice/technician/jobber/JobberTimesheet.vue'),
        meta: { title: 'Timesheet' }
      },
      {
        // Jobber Original "Search" tab (same v2-shell rationale as above).
        path: 'search',
        name: 'TechSearch',
        component: () => import('@/modules/fieldservice/technician/jobber/JobberSearch.vue'),
        meta: { title: 'Search' }
      },
      {
        path: 'notifications',
        name: 'TechNotifications',
        component: () => import('@/modules/fieldservice/technician/TechNotifications.vue'),
        meta: { title: 'Notifications' }
      },
      {
        path: 'tasks',
        name: 'TechTasks',
        component: () => import('@/modules/fieldservice/technician/TechTasks.vue'),
        meta: { title: 'Tasks' }
      },
      {
        // HCP "View My Invoices" screen. Rendered by the config-driven v2 shell
        // (ScreenRenderer -> hcp_invoices widget); this route only needs to
        // exist so router.push('/technician/invoices') matches. The component
        // is a graceful fallback for any non-v2 mount of this path.
        path: 'invoices',
        name: 'TechInvoices',
        component: () => import('@/modules/fieldservice/technician/hcp/HcpInvoices.vue'),
        meta: { title: 'My Invoices' }
      },
      {
        path: 'truck-stock',
        name: 'TechTruckStock',
        component: () => import('@/modules/fieldservice/technician/TechTruckStock.vue'),
        meta: { title: 'Truck Stock' }
      },
      {
        // FSJ-6-00070 Phase 3 — Techville Vehicle Stock TZ (My Stock / Purchase / Count).
        path: 'vehicle-stock',
        name: 'TechVehicleStock',
        component: () => import('@/modules/fieldservice/technician/TechVehicleStock.vue'),
        meta: { title: 'Vehicle Stock' }
      },
      {
        path: 'event/:id',
        name: 'TechEventDetail',
        component: () => import('@/modules/fieldservice/technician/TechEventDetail.vue'),
        meta: { title: 'Event Details' }
      },
      {
        path: 'activity-log',
        name: 'TechActivityLog',
        component: () => import('@/modules/fieldservice/technician/TechActivityLog.vue'),
        meta: { title: 'Activity Log' }
      },
      {
        // B4: My Requests in the field app — every worker (incl. technician)
        // can view/raise HR + time-off requests. Uses hr.me.* self-service API.
        path: 'my-requests',
        name: 'TechMyRequests',
        component: () => import('@/modules/fieldservice/technician/TechMyRequests.vue'),
        meta: { title: 'My Requests' }
      },
      {
        // TV-1-00508: assigned courses inside the technician shell. The office
        // LMS layout is not used — its sidebar lists the whole catalog.
        path: 'learning',
        name: 'TechMyLearning',
        component: () => import('@/modules/lms/views/MyLearningView.vue'),
        meta: {
          title: 'My Learning',
          assignedOnly: true,
          learningPlayer: '/technician/learning',
          learningHome: '/technician/learning',
        }
      },
      {
        path: 'learning/:id',
        name: 'TechCoursePlayer',
        component: () => import('@/modules/lms/views/CoursePlayerView.vue'),
        meta: {
          title: 'Course',
          assignedOnly: true,
          learningHome: '/technician/learning',
        }
      }
    ]
  },
  // ========== FIELD SERVICE ADMIN (Interface Builder) ==========
  {
    path: '/settings/field-service',
    name: 'FieldServiceSettings',
    component: () => import('@/modules/fieldservice/admin/InterfaceBuilder.vue'),
    meta: { requiresAuth: true, title: 'Interface Builder', permission: 'settings.field_service' }
  },
  {
    path: '/settings/activity-events',
    name: 'ActivityEventsSettings',
    component: () => import('@/modules/fieldservice/admin/ActivityEventsPage.vue'),
    meta: { requiresAuth: true, title: 'Activity Events', permission: 'settings.field_service' }
  },
  {
    path: '/settings/checklist-items',
    name: 'ChecklistItemsSettings',
    component: () => import('@/modules/fieldservice/admin/ChecklistItemsPage.vue'),
    meta: { requiresAuth: true, title: 'Checklist Items', permission: 'settings.field_service' }
  },
  {
    path: '/settings/tech-payment-methods',
    name: 'TechPaymentMethodsSettings',
    component: () => import('@/modules/fieldservice/admin/TechPaymentMethodsPage.vue'),
    meta: { requiresAuth: true, title: 'Technician Payment Methods', permission: 'settings.field_service' }
  },
  {
    path: '/settings/mobile-app',
    name: 'MobileAppSettings',
    component: () => import('@/modules/fieldservice/admin/MobileAppSettingsPage.vue'),
    meta: { requiresAuth: true, title: 'Mobile App Settings', permission: 'settings.field_service' }
  },
  {
    path: '/settings/tech-preview',
    name: 'TechAppPreview',
    component: () => import('@/modules/fieldservice/admin/TechAppPreviewPage.vue'),
    meta: { requiresAuth: true, title: 'Tech App Preview', permission: 'settings.field_service' }
  },
  {
    // Company-side lifecycle management of its own mobile interfaces
    // (activate / pause / remove), respecting HQ locks.
    path: '/settings/interfaces',
    name: 'CompanyInterfaces',
    component: () => import('@/modules/fieldservice/admin/CompanyInterfacesView.vue'),
    meta: { requiresAuth: true, title: 'Interfaces', permission: 'settings.field_service' }
  },
  {
    path: '/settings/workflow',
    name: 'WorkflowBuilder',
    component: () => import('@/modules/workflow/WorkflowBuilderPage.vue'),
    meta: { requiresAuth: true, title: 'Workflow Builder', permission: 'workflow.view' }
  },
  {
    path: '/settings/office-constructor',
    name: 'OfficeJobConstructor',
    component: () => import('@/modules/jobs/constructor/OfficeConstructorPage.vue'),
    meta: { requiresAuth: true, title: 'Office Job Constructor', permission: 'office.constructor.manage' }
  },
  {
    path: '/settings/service-lines',
    name: 'ServiceLines',
    component: () => import('@/modules/workflow/ServiceLinesSettingsView.vue'),
    meta: { requiresAuth: true, title: 'Lines of Business', permission: 'workflow.view' }
  },
  {
    path: '/settings/job-statuses',
    name: 'JobStatuses',
    component: () => import('@/modules/workflow/JobStatusesSettingsView.vue'),
    meta: { requiresAuth: true, title: 'Job Statuses', permission: 'workflow.view' }
  },
  {
    path: '/workflow/payroll',
    name: 'WorkflowPayroll',
    component: () => import('@/modules/workflow/WorkflowPayrollPage.vue'),
    meta: { requiresAuth: true, title: 'Payroll Finalization', permission: 'payroll.view' }
  },
  // ========== FIELD SERVICE — PAYROLL (configurable pay models) ==========
  {
    path: '/field-service/payroll/report',
    name: 'FieldPayrollReport',
    component: () => import('@/modules/fieldservice/payroll/PayrollReportView.vue'),
    meta: { requiresAuth: true, title: 'Technician Payroll', permission: 'payroll.view' }
  },
  {
    path: '/field-service/payroll/models',
    name: 'FieldPayModels',
    component: () => import('@/modules/fieldservice/payroll/PayModelsView.vue'),
    meta: { requiresAuth: true, title: 'Pay Models', permission: 'payroll.manage' }
  },
  {
    path: '/field-service/payroll/assignments',
    name: 'FieldPayModelAssignments',
    component: () => import('@/modules/fieldservice/payroll/PayModelAssignmentsView.vue'),
    meta: { requiresAuth: true, title: 'Pay Model Assignments', permission: 'payroll.manage' }
  },
  // Finance menu aliases — canonical routes remain /field-service/payroll/*
  {
    path: '/finance/payroll/report',
    redirect: '/field-service/payroll/report'
  },
  {
    path: '/finance/payroll/models',
    redirect: '/field-service/payroll/models'
  },
  {
    path: '/finance/payroll/assignments',
    redirect: '/field-service/payroll/assignments'
  },
  // ========== LMS MODULE (Learning Management) ==========
  {
    path: '/lms', // permission: 'lms.view'
    name: 'LMS',
    component: () => import('@/modules/lms/LMSView.vue'),
    meta: { requiresAuth: true, title: 'Learning', permission: ['lms.view', 'lms.training_only'] },
    children: [
      { path: '', redirect: '/lms/my-learning' },
      {
        path: 'my-learning',
        name: 'LMSMyLearning',
        component: () => import('@/modules/lms/views/MyLearningView.vue'),
        meta: { title: 'My Learning', permission: ['lms.view', 'lms.training_only'] } // scanned as permission: 'lms.view'
      },
      {
        path: 'learn/:id',
        name: 'LMSCoursePlayer',
        component: () => import('@/modules/lms/views/CoursePlayerView.vue'),
        meta: { title: 'Course', permission: ['lms.view', 'lms.training_only'] } // scanned as permission: 'lms.view'
      },
      {
        path: 'courses',
        name: 'LMSCourses',
        component: () => import('@/modules/lms/views/CoursesView.vue'),
        meta: { title: 'Courses', permission: 'lms.view' }
      },
      {
        path: 'courses/:id/builder',
        name: 'LMSCourseBuilder',
        component: () => import('@/modules/lms/views/CourseBuilderView.vue'),
        meta: { title: 'Course Builder', permission: 'lms.course.edit' }
      },
      {
        path: 'courses/:courseId/lessons/:lessonId/edit',
        name: 'LMSLessonEditor',
        component: () => import('@/modules/lms/views/LessonEditorView.vue'),
        meta: { title: 'Lesson Editor', permission: 'lms.course.edit' }
      },
      {
        path: 'progress',
        name: 'LMSTeamProgress',
        component: () => import('@/modules/lms/views/TeamProgressView.vue'),
        meta: { title: 'Team Progress', permission: ['lms.progress.view_team', 'lms.progress.view_all'] }
      },
      {
        path: 'analytics',
        name: 'LMSAnalytics',
        component: () => import('@/modules/lms/views/AnalyticsView.vue'),
        meta: { title: 'Analytics', permission: ['lms.progress.view_team', 'lms.progress.view_all'] }
      },
      {
        path: 'reviews',
        name: 'LMSReviews',
        component: () => import('@/modules/lms/views/SubmissionReviewView.vue'),
        meta: { title: 'Reviews', permission: 'lms.submission.review' }
      },
      {
        path: 'tracks',
        name: 'LMSTracks',
        component: () => import('@/modules/lms/views/TracksView.vue'),
        meta: { title: 'Learning Tracks', permission: 'lms.track.view' }
      },
      {
        path: 'tracks/:id',
        name: 'LMSTrackMap',
        component: () => import('@/modules/lms/views/TrackMapView.vue'),
        meta: { title: 'Track', permission: 'lms.track.view' }
      },
      {
        path: 'certificates',
        name: 'LMSCertificates',
        component: () => import('@/modules/lms/views/MyCertificatesView.vue'),
        meta: { title: 'My Certificates', permission: 'lms.certificate.view' }
      }
    ]
  },

  // ========== HR MODULE (Human Resources) ==========
  {
    path: '/hr',
    name: 'HR',
    component: () => import('@/modules/hr/HRView.vue'),
    meta: { requiresAuth: true, title: 'HR', permission: 'hr.view' },
    children: [
      {
        path: '',
        redirect: '/hr/dashboard'
      },
      {
        path: 'dashboard',
        name: 'HRDashboard',
        component: () => import('@/modules/hr/views/HRDashboardView.vue'),
        meta: { title: 'HR Dashboard', permission: 'hr.view' }
      },
      {
        path: 'my-portal',
        name: 'HRMyPortal',
        component: () => import('@/modules/hr/views/MyHrPortalView.vue'),
        meta: { title: 'My Requests', permission: 'hr.me.view' }
      },
      {
        // B1: HR request queue — administer generic employee requests, filter
        // by department. Separate from the self-service /hr/my-portal above.
        path: 'requests',
        name: 'HRRequestsQueue',
        component: () => import('@/modules/hr/views/HrRequestsQueueView.vue'),
        meta: { title: 'Requests', permission: 'hr.requests.view' }
      },
      {
        path: 'recruiting',
        name: 'HRRecruiting',
        component: () => import('@/modules/hr/views/RecruitingView.vue'),
        meta: { title: 'Recruiting', permission: 'hr.recruiting.view' }
      },
      {
        // Pt.4: shareable deep link that opens the candidate card
        path: 'candidates/:candidateId',
        name: 'HRCandidate',
        component: () => import('@/modules/hr/views/RecruitingView.vue'),
        meta: { title: 'Candidate', permission: 'hr.recruiting.view' }
      },
      {
        // A3: shareable deep link that opens a vacancy board/page
        path: 'recruiting/jobs/:jobId',
        name: 'HRRecruitingJob',
        component: () => import('@/modules/hr/views/RecruitingView.vue'),
        meta: { title: 'Vacancy', permission: 'hr.recruiting.view' }
      },
      {
        // A9: AI scoring analytics (agreement %, accuracy by vacancy, false pos/neg)
        path: 'recruiting/ai-analytics',
        name: 'HRRecruitingAiAnalytics',
        component: () => import('@/modules/hr/views/RecruitingAiAnalyticsView.vue'),
        meta: { title: 'AI Scoring Analytics', permission: 'hr.recruiting.view' }
      },
      {
        // A8: work.ua integration (credentials, link/publish/sync vacancies)
        path: 'recruiting/integrations',
        name: 'HRRecruitingIntegrations',
        component: () => import('@/modules/hr/views/RecruitingIntegrationsView.vue'),
        meta: { title: 'Recruiting Integrations', permission: 'hr.recruiting.view' }
      },
      {
        path: 'org-structure',
        name: 'OrgStructure',
        component: () => import('@/modules/hr/views/OrgStructureView.vue'),
        meta: { title: 'Organization Structure', permission: 'hr.org_structure' }
      },
      {
        path: 'legal-structure',
        name: 'LegalStructure',
        component: () => import('@/modules/hr/views/LegalStructureView.vue'),
        meta: { title: 'Legal Structure', permission: 'hr.legal_structure' }
      },
      {
        path: 'employees',
        name: 'HREmployees',
        component: () => import('@/modules/hr/views/EmployeesView.vue'),
        meta: { title: 'Employees', permission: 'hr.employees' }
      },
      {
        path: 'employees/:id',
        name: 'HREmployeeDetail',
        component: () => import('@/modules/hr/views/EmployeeDetailView.vue'),
        meta: { title: 'Employee Detail', permission: 'hr.employees' }
      },
      {
        path: 'time-off',
        name: 'HRTimeOff',
        component: () => import('@/modules/hr/views/TimeOffView.vue'),
        meta: { title: 'Time Off', permission: 'hr.time_off.view' }
      },
      {
        path: 'calendar',
        name: 'HRCalendar',
        component: () => import('@/modules/hr/views/HrCalendarView.vue'),
        meta: { title: 'HR Calendar', permission: 'hr.calendar.view' }
      },
      {
        path: 'documents',
        name: 'HRDocuments',
        component: () => import('@/modules/hr/views/HRDocumentsView.vue'),
        meta: { title: 'HR Documents', permission: 'hr.employees' }
      },
      {
        path: 'workforce',
        name: 'HRWorkforce',
        component: () => import('@/modules/hr/views/WorkforceView.vue'),
        meta: { title: 'Workforce', permission: 'hr.workforce.view' }
      },
      {
        path: 'advanced',
        name: 'HRAdvanced',
        component: () => import('@/modules/hr/views/AdvancedHrView.vue'),
        meta: { title: 'Advanced HRM', permission: 'hr.advanced.view' }
      },
      {
        path: 'performance-plus',
        name: 'HRPerformancePlus',
        component: () => import('@/modules/hr/views/HrPerformancePlusView.vue'),
        meta: { title: 'Performance Plus', permission: 'hr.advanced.view' }
      },
      {
        path: 'surveys',
        name: 'HRSurveys',
        component: () => import('@/modules/hr/views/SurveysView.vue'),
        meta: { title: 'Surveys', permission: 'hr.advanced.view' }
      },
      {
        path: 'statistics',
        name: 'HRStatistics',
        component: () => import('@/modules/hr/views/HrStatisticsView.vue'),
        meta: { title: 'HR Statistics', permission: 'hr.advanced.view' }
      },
      {
        path: 'offboarding',
        name: 'HROffboarding',
        component: () => import('@/modules/hr/views/OffboardingHubView.vue'),
        meta: { title: 'Onboarding / Offboarding', permission: 'hr.offboarding.view' }
      },
      {
        path: 'exit-interviews',
        name: 'HRExitInterviews',
        component: () => import('@/modules/hr/views/ExitInterviewsView.vue'),
        meta: { title: 'Exit Interviews', permission: ['hr.exit_survey.view', 'hr.offboarding.view'] }
      },
      {
        path: 'hurma-journal',
        name: 'HRHurmaJournal',
        component: () => import('@/modules/hr/views/HurmaJournalView.vue'),
        meta: { title: 'Hurma Journal', permission: 'hr.hurma_migration.view' }
      },
      {
        path: 'hurma-recruiting',
        name: 'HRHurmaRecruiting',
        component: () => import('@/modules/hr/views/HurmaRecruitingView.vue'),
        meta: { title: 'Hurma Recruiting', permission: 'hr.hurma_migration.view' }
      },
      {
        path: 'technicians',
        redirect: '/production/technicians',
      },
      {
        path: 'technicians/:id',
        redirect: to => ({ path: `/production/technicians/${to.params.id}` }),
      },
      {
        path: 'positions',
        name: 'HRPositions',
        component: () => import('@/modules/hr/views/PositionsView.vue'),
        meta: { title: 'Job Positions', permission: 'hr.manage_positions' }
      },
      {
        path: 'unit-types',
        name: 'HRUnitTypes',
        component: () => import('@/modules/hr/views/UnitTypesView.vue'),
        meta: { title: 'Organization Unit Types', permission: 'hr.manage_settings' }
      }
    ]
  },
  // ========== ASSETS → OPERATIONS REDIRECTS ==========
  { path: '/assets', redirect: '/operations/inventory' },
  { path: '/assets/warehouses', redirect: '/operations/warehouses' },
  { path: '/assets/items', redirect: '/operations/inventory' },
  { path: '/assets/stock', redirect: '/operations/inventory' },
  { path: '/assets/movements', redirect: '/operations/inventory' },
  { path: '/assets/categories', redirect: '/operations/inventory' },
  { path: '/assets/suppliers', redirect: '/operations/suppliers' },
  // ========== JOBS MODULE ==========
  {
    path: '/jobs',
    name: 'Jobs',
    component: () => import('@/modules/jobs/JobsView.vue'),
    meta: { requiresAuth: true, title: 'Jobs', permission: 'jobs.view' },
    children: [
      {
        path: '',
        redirect: '/jobs/board'
      },
      {
        path: 'new',
        name: 'NewJob',
        component: () => import('@/modules/jobs/views/NewJobView.vue'),
        meta: { title: 'New Job', permission: 'jobs.create' }
      },
      {
        // Create Job v2 — isolated new-design wizard (Artem Figma "1.0 Create
        // job"). Purely additive; the proven /jobs/new above is untouched.
        path: 'new-v2',
        name: 'NewJobV2',
        component: () => import('@/modules/jobs/views/NewJobV2View.vue'),
        meta: { title: 'New Job v2', permission: 'jobs.create', hideModuleSubmenu: true }
      },
      {
        path: 'board',
        name: 'JobBoard',
        component: () => import('@/modules/jobs/views/JobBoardView.vue'),
        meta: { title: 'All Jobs', permission: 'jobs.view' }
      },
      {
        // All Jobs v2 — isolated new-design board (Artem Figma "1.0 All job").
        // Purely additive; the /jobs/board above is untouched.
        path: 'board-v2',
        name: 'JobBoardV2',
        component: () => import('@/modules/jobs/views/JobBoardV2View.vue'),
        meta: { title: 'All Jobs v2', permission: 'jobs.view', hideModuleSubmenu: true }
      },
      {
        path: 'schedule-v2',
        name: 'JobScheduleV2',
        component: () => import('@/modules/jobs/views/ScheduleV2View.vue'),
        meta: { title: 'Schedule v2', permission: 'jobs.view' }
      },
      {
        path: 'schedule',
        name: 'JobSchedule',
        component: () => import('@/modules/jobs/views/JobScheduleView.vue'),
        meta: { title: 'Schedule', permission: 'jobs.view' }
      },
      {
        // Schedule (new design) — isolated new-design week board (Artem Figma
        // "1.0  Schedule"). Purely additive; /jobs/schedule and /jobs/schedule-v2
        // above are untouched. Reuses the same schedule data layer.
        path: 'schedule-newdesign',
        name: 'JobScheduleNewDesign',
        component: () => import('@/modules/jobs/views/JobScheduleNewDesignView.vue'),
        meta: { title: 'Schedule (new)', permission: 'jobs.view', hideModuleSubmenu: true }
      },
      {
        path: 'st/:stId',
        name: 'STJobDetail',
        component: () => import('@/modules/jobs/views/STJobDetailView.vue'),
        meta: { title: 'ServiceTitan Job', permission: 'jobs.view' }
      },
      {
        path: 'settings/job-types',
        name: 'JobTypes',
        component: () => import('@/modules/jobs/views/settings/JobTypesView.vue'),
        meta: { title: 'Job Types', permission: 'jobs.manage_settings' }
      },
      {
        path: 'settings/job-types/new',
        name: 'JobTypeCreate',
        component: () => import('@/modules/jobs/views/settings/JobTypeEditView.vue'),
        meta: { title: 'New Job Type', permission: 'jobs.manage_settings' }
      },
      {
        path: 'settings/job-types/:id',
        name: 'JobTypeEdit',
        component: () => import('@/modules/jobs/views/settings/JobTypeEditView.vue'),
        meta: { title: 'Edit Job Type', permission: 'jobs.manage_settings' }
      },
      {
        path: 'settings/business-units',
        name: 'BusinessUnits',
        component: () => import('@/modules/jobs/views/settings/BusinessUnitsView.vue'),
        meta: { title: 'Business Units', permission: 'jobs.manage_settings' }
      },
      {
        path: 'settings/doc-templates',
        name: 'DocTemplates',
        component: () => import('@/modules/jobs/views/settings/DocTemplatesView.vue'),
        meta: { title: 'Job Form Templates', permission: 'jobs.manage_settings' }
      },
      {
        path: 'settings/calculator',
        name: 'CalculatorSettings',
        component: () => import('@/modules/jobs/views/settings/CleaningCalcSettingsView.vue'),
        meta: { title: 'Price & Duration Calculator', permission: 'jobs.manage_settings' }
      },
      {
        path: 'settings/cleaning-calculator',
        name: 'CleaningCalcSettings',
        redirect: '/jobs/settings/calculator',
        meta: { title: 'Price & Duration Calculator', permission: 'jobs.manage_settings' }
      },
      {
        path: 'settings/parts-markup',
        name: 'PartsMarkupSettings',
        component: () => import('@/modules/jobs/views/settings/PartsMarkupSettingsView.vue'),
        meta: { title: 'Parts Markup', permission: 'jobs.manage_settings' }
      },
      {
        path: 'settings/parts-flows',
        name: 'PartsFlowsList',
        component: () => import('@/modules/jobs/views/settings/PartsFlowsListView.vue'),
        meta: { title: 'Parts Flows', permission: 'parts.manage_flows' }
      },
      {
        path: 'settings/parts-flows/new',
        name: 'PartsFlowCreate',
        component: () => import('@/modules/jobs/views/settings/PartsFlowEditView.vue'),
        meta: { title: 'New Parts Flow', permission: 'parts.manage_flows' }
      },
      {
        path: 'settings/parts-flows/:id',
        name: 'PartsFlowEdit',
        component: () => import('@/modules/jobs/views/settings/PartsFlowEditView.vue'),
        meta: { title: 'Edit Parts Flow', permission: 'parts.manage_flows' }
      },
      {
        path: 'settings/parts-pricing',
        name: 'PartsPricingSettings',
        component: () => import('@/modules/jobs/views/settings/PartsPricingSettingsView.vue'),
        meta: { title: 'Parts Pricing', permission: 'parts.manage_pricing' }
      },
      {
        path: 'settings/cleaning-operations',
        name: 'CleaningOpsSettings',
        component: () => import('@/modules/jobs/views/settings/CleaningOpsSettingsView.vue'),
        meta: { title: 'Cleaning Operations', permission: 'jobs.manage_settings' }
      },
      {
        path: 'analytics/plan-vs-actual',
        name: 'PlanVsActual',
        component: () => import('@/modules/jobs/views/settings/PlanVsActualView.vue'),
        meta: { title: 'Plan vs Actual', permission: 'jobs.view' }
      },
      {
        path: 'settings/business-units/new',
        name: 'BusinessUnitCreate',
        component: () => import('@/modules/jobs/views/settings/BusinessUnitEditView.vue'),
        meta: { title: 'New Business Unit', permission: 'jobs.manage_settings' }
      },
      {
        path: 'settings/business-units/:id',
        name: 'BusinessUnitEdit',
        component: () => import('@/modules/jobs/views/settings/BusinessUnitEditView.vue'),
        meta: { title: 'Edit Business Unit', permission: 'jobs.manage_settings' }
      },
      {
        path: 'settings/campaigns',
        name: 'JobCampaigns',
        component: () => import('@/modules/jobs/views/settings/CampaignsView.vue'),
        meta: { title: 'Campaigns', permission: 'jobs.manage_settings' }
      },
      {
        path: 'settings/arrival-windows',
        name: 'ArrivalWindows',
        component: () => import('@/modules/jobs/views/settings/ArrivalWindowsView.vue'),
        meta: { title: 'Arrival Windows', permission: 'jobs.manage_settings' }
      },
      {
        path: 'settings/tags',
        name: 'JobTags',
        component: () => import('@/modules/jobs/views/settings/TagsView.vue'),
        meta: { title: 'Tags', permission: 'jobs.manage_settings' }
      },
      {
        path: 'settings/pricebook',
        name: 'Pricebook',
        component: () => import('@/modules/jobs/views/settings/PricebookView.vue'),
        meta: { title: 'Pricebook', permission: 'finance.manage_pricebook' }
      },
      {
        path: 'settings/summary-templates',
        name: 'SummaryTemplates',
        component: () => import('@/modules/jobs/views/settings/SummaryTemplatesView.vue'),
        meta: { title: 'Summary Templates', permission: 'jobs.edit_summary_templates' }
      },
      {
        path: 'settings/cancel-reasons',
        name: 'CancelReasons',
        component: () => import('@/modules/jobs/views/settings/CancelReasonsView.vue'),
        meta: { title: 'Cancellation Reasons', permission: 'jobs.manage_settings' }
      },
      {
        path: 'settings/brands',
        name: 'JobBrands',
        component: () => import('@/modules/jobs/views/settings/BrandsView.vue'),
        meta: { title: 'Brands', permission: 'jobs.manage_settings' }
      },
      {
        path: 'settings/timesheet-codes',
        name: 'TimesheetCodes',
        component: () => import('@/modules/jobs/views/settings/TimesheetCodesView.vue'),
        meta: { title: 'Timesheet Codes', permission: 'jobs.manage_settings' }
      },
      {
        // Job Details v2 — isolated new-design detail (Artem Figma "1.1 Job
        // details - full screen"). Purely additive; the /jobs/:id detail above
        // is untouched. A DETAIL page reached by direct URL /jobs/<id>/v2 (no
        // top-level menu item); hideModuleSubmenu keeps the module flyout hidden.
        path: ':id/v2',
        name: 'JobDetailV2',
        component: () => import('@/modules/jobs/views/JobDetailV2View.vue'),
        meta: { requiresAuth: true, title: 'Job Details v2', permission: 'jobs.view', hideModuleSubmenu: true }
      },
      {
        path: ':id',
        name: 'JobDetail',
        component: () => import('@/modules/jobs/views/JobDetailView.vue'),
        meta: { title: 'Job Detail', permission: 'jobs.view' }
      }
    ]
  },
  // Legacy redirects: /territories -> /production
  {
    path: '/territories',
    redirect: '/production/projects'
  },
  {
    path: '/territories/technicians',
    redirect: '/production/technicians'
  },
  {
    path: '/territories/technicians/:id',
    redirect: to => ({ path: `/production/technicians/${to.params.id}` })
  },
  {
    path: '/territories/:pathMatch(.*)*',
    redirect: to => ({ path: `/production/${to.params.pathMatch}` })
  },
  // Dev / Test pages
  {
    path: '/dev/document-editor',
    name: 'DevDocumentEditor',
    component: () => import('@/modules/dev/DocumentEditorTestPage.vue'),
    meta: { requiresAuth: true, title: 'Document Editor Test' }
  },

  // ========== APPLICATION ADMINISTRATION (Global Admin only) ==========
  {
    path: '/app-admin',
    name: 'AppAdminHome',
    component: () => import('@/views/app-admin/AppAdminHomeView.vue'),
    meta: { requiresAuth: true, title: 'Application Administration', permission: 'admin.app.view' }
  },
  {
    path: '/app-admin/spend',
    name: 'AppAdminSpend',
    component: () => import('@/views/app-admin/RealInfraSpendView.vue'),
    meta: { requiresAuth: true, title: 'Real Infra Spend', permission: 'admin.app.view' }
  },
  {
    path: '/app-admin/currencies',
    name: 'AppAdminCurrencies',
    component: () => import('@/views/app-admin/CurrencyCatalogView.vue'),
    meta: { requiresAuth: true, title: 'Currency Catalog', permission: 'admin.billing.view' }
  },
  {
    path: '/app-admin/compute-units',
    name: 'AppAdminComputeUnits',
    component: () => import('@/views/app-admin/ComputeUnitsView.vue'),
    meta: { requiresAuth: true, title: 'Compute Units', permission: 'admin.billing.view' }
  },
  {
    path: '/app-admin/operations',
    name: 'AppAdminOperations',
    component: () => import('@/views/app-admin/OperationsCatalogView.vue'),
    meta: { requiresAuth: true, title: 'Operations Catalog', permission: 'admin.billing.view' }
  },
  {
    path: '/app-admin/vendor-costs',
    name: 'AppAdminVendorCosts',
    component: () => import('@/views/app-admin/VendorCostsView.vue'),
    meta: { requiresAuth: true, title: 'Vendor Costs', permission: 'admin.billing.view' }
  },
  // FSJ-6-00047: Platform HQ billing — clients & contracts, tariff catalog, calculator.
  {
    path: '/app-admin/billing',
    name: 'AppAdminBillingClients',
    component: () => import('@/views/app-admin/billing/HqBillingClientsView.vue'),
    meta: { requiresAuth: true, title: 'Billing — Clients', permission: 'admin.billing.view' }
  },
  {
    path: '/app-admin/billing/catalog',
    name: 'AppAdminBillingCatalog',
    component: () => import('@/views/app-admin/billing/HqBillingCatalogView.vue'),
    meta: { requiresAuth: true, title: 'Tariff Catalog', permission: 'admin.billing.view' }
  },
  {
    path: '/app-admin/billing/calculator',
    name: 'AppAdminBillingCalculator',
    component: () => import('@/views/app-admin/billing/HqBillingCalculatorView.vue'),
    meta: { requiresAuth: true, title: 'Pricing Calculator', permission: 'admin.billing.view' }
  },
  {
    path: '/app-admin/billing/clients/:domainId(\\d+)',
    name: 'AppAdminBillingClientCard',
    component: () => import('@/views/app-admin/billing/HqBillingClientCardView.vue'),
    meta: { requiresAuth: true, title: 'Client Billing', permission: 'admin.billing.view' }
  },
  {
    path: '/app-admin/billing/invoices/:id(\\d+)',
    name: 'AppAdminBillingInvoiceStatement',
    component: () => import('@/modules/billing/views/InvoiceStatementView.vue'),
    meta: { requiresAuth: true, title: 'Invoice', permission: 'admin.billing.view', statementScope: 'hq' }
  },
  {
    path: '/app-admin/settings',
    name: 'AppAdminPlatformSettings',
    component: () => import('@/views/app-admin/PlatformSettingsView.vue'),
    meta: { requiresAuth: true, title: 'Platform Settings', permission: 'admin.billing.view' }
  },
  {
    // HQ cross-company tool: copy a mobile interface + office + Flow into any
    // company. Platform-only (under /app-admin prefix -> requiresPlatform guard).
    path: '/app-admin/interface-transfer',
    name: 'AppAdminInterfaceTransfer',
    component: () => import('@/views/app-admin/InterfaceTransferView.vue'),
    meta: { requiresAuth: true, requiresPlatform: true, title: 'Interface Transfer' }
  },
  {
    // HQ overview + governance of every company's interfaces.
    path: '/app-admin/interfaces',
    name: 'AppAdminInterfacesByCompany',
    component: () => import('@/views/app-admin/InterfacesByCompanyView.vue'),
    meta: { requiresAuth: true, requiresPlatform: true, title: 'Interfaces by Company' }
  },
  {
    path: '/app-admin/wallets',
    name: 'AppAdminWallets',
    component: () => import('@/views/app-admin/WalletsView.vue'),
    meta: { requiresAuth: true, title: 'Wallets & Top-up', permission: 'admin.billing.view' }
  },
  {
    path: '/app-admin/payment-fees',
    name: 'AppAdminPaymentFees',
    component: () => import('@/views/app-admin/PaymentFeesView.vue'),
    meta: { requiresAuth: true, title: 'Payment Fees', permission: 'admin.payments.view' }
  },
  {
    path: '/app-admin/live-feed',
    name: 'AppAdminLiveFeed',
    component: () => import('@/views/app-admin/LiveActivityFeedView.vue'),
    meta: { requiresAuth: true, title: 'Live Activity Feed', permission: 'admin.billing.view' }
  },
  {
    path: '/app-admin/coverage',
    name: 'AppAdminCoverage',
    component: () => import('@/views/app-admin/CoverageMapView.vue'),
    meta: { requiresAuth: true, title: 'Billing Coverage Map', permission: 'admin.billing.view' }
  },
  {
    path: '/app-admin/tiers',
    name: 'AppAdminTiers',
    component: () => import('@/views/app-admin/TiersView.vue'),
    meta: { requiresAuth: true, title: 'IP Value Tiers', permission: 'admin.billing.view' }
  },
  {
    path: '/app-admin/pnl',
    name: 'AppAdminPnL',
    component: () => import('@/views/app-admin/PnLDashboardView.vue'),
    meta: { requiresAuth: true, title: 'P&L Dashboard', permission: 'admin.billing.view' }
  },
  {
    path: '/app-admin/voice-billing',
    name: 'AppAdminVoiceBilling',
    component: () => import('@/views/app-admin/VoiceBillingView.vue'),
    meta: { requiresAuth: true, title: 'Voice Billing', permission: 'admin.billing.view' }
  },
  {
    path: '/app-admin/communication-usage',
    name: 'AppAdminCommunicationUsage',
    component: () => import('@/views/app-admin/CommunicationUsageView.vue'),
    meta: { requiresAuth: true, title: 'AI Communication Usage', permission: 'admin.billing.view' }
  },
  {
    path: '/app-admin/voice-summary-usage',
    name: 'AppAdminVoiceSummaryUsage',
    component: () => import('@/views/app-admin/VoiceSummaryUsageView.vue'),
    meta: { requiresAuth: true, title: 'Voice Summary Usage', permission: 'admin.billing.view' }
  },
  {
    path: '/app-admin/telnyx-numbers',
    name: 'AppAdminTelnyxNumbers',
    component: () => import('@/views/app-admin/TelnyxNumbersRegistryView.vue'),
    meta: { requiresAuth: true, title: 'Telephony Numbers Registry', permission: 'admin.telnyx.view' }
  },
  {
    path: '/app-admin/tech-error-logs',
    name: 'AppAdminTechErrorLogs',
    component: () => import('@/views/app-admin/TechErrorLogsView.vue'),
    meta: { requiresAuth: true, title: 'Tech Error Logs', permission: 'admin.tech_errors.view' }
  },

  // ========== CUSTOMER PORTAL (standalone, own auth) ==========
  {
    path: '/portal',
    component: () => import('@/modules/customer-portal/CustomerPortalApp.vue'),
    meta: { isPortalRoute: true },
    children: [
      {
        path: '',
        redirect: '/portal/login'
      },
      {
        path: 't/:magicToken',
        name: 'PortalMagicLink',
        component: () => import('@/modules/customer-portal/views/PortalAuth.vue'),
        meta: { title: 'Customer Portal', isPortalRoute: true },
        props: true
      },
      {
        path: 'login',
        name: 'PortalAuth',
        component: () => import('@/modules/customer-portal/views/PortalAuth.vue'),
        meta: { title: 'Customer Portal', isPortalRoute: true }
      },
      {
        path: 'dashboard',
        name: 'PortalDashboard',
        component: () => import('@/modules/customer-portal/views/PortalDashboard.vue'),
        meta: { title: 'My Services', isPortalRoute: true, requiresPortalAuth: true }
      },
      {
        path: 'services/:jobId',
        name: 'PortalServiceDetail',
        component: () => import('@/modules/customer-portal/views/PortalServiceDetail.vue'),
        meta: { title: 'Service Details', isPortalRoute: true, requiresPortalAuth: true }
      },
    ]
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Remote Training candidates have no dashboard. Everyone else keeps /dashboard,
// including people who lack dashboard.view and are let through to avoid a loop.
function officeHomePath(authStore) {
  if (authStore.hasPermission('lms.training_only') && !authStore.hasPermission('dashboard.view')) {
    return '/lms/my-learning'
  }
  return '/dashboard'
}

// Navigation guard
router.beforeEach(async (to, from, next) => {
  // Customer portal routes bypass main auth entirely
  if (to.meta.isPortalRoute) {
    if (to.meta.requiresPortalAuth) {
      const portalToken = sessionStorage.getItem('portal_token')
      if (!portalToken) {
        console.log('[Router] Portal auth required, redirecting to portal login')
        next('/portal/login')
        return
      }
    }
    next()
    return
  }

  const authStore = useAuthStore()

  // Capgo OTA (and any WebView reload) wipes the in-memory access token. Boot
  // silent-refresh repopulates it, but Pinia's isAuthenticated flag is set at
  // store create — before that refresh. Waiting here keeps a live technician
  // on /technician after an OTA apply instead of bouncing them to /login.
  // Intentionally only for requiresAuth routes: /login must not await+promote,
  // or biometric Sign Out (full reload onto /login with a preserved refresh)
  // would skip the fingerprint gate.
  if (to.meta.requiresAuth) {
    try { await awaitBootGate() } catch { /* never block navigation */ }
    try { authStore.loadFromStorage() } catch { /* storage unavailable */ }
  }

  // Check if this is a technician session
  const isTechnicianSession = localStorage.getItem('is_technician_session') === 'true'
  
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    console.log('[Router] Not authenticated, redirecting to login')
    // Preserve the intended destination so deep links (e.g. a ticket link
    // from Telegram) survive the login round-trip.
    if (to.fullPath && to.fullPath !== '/' && to.path !== '/login') {
      next({ path: '/login', query: { redirect: to.fullPath } })
    } else {
      next('/login')
    }
  } else if (to.path === '/login' && authStore.isAuthenticated) {
    if (isTechnicianSession) {
      console.log('[Router] Technician session, redirecting to /technician')
      next('/technician')
    } else {
      console.log('[Router] Regular session, redirecting to', officeHomePath(authStore))
      next(officeHomePath(authStore))
    }
  } else if (isTechnicianSession && authStore.isAuthenticated) {
    const isGoingToTechRoute = to.path.startsWith('/technician')
    const isGoingToLogin = to.path === '/login'
    const isGoingToPublicLegal = TECH_SESSION_PUBLIC_PATHS.has(to.path)

    if (!isGoingToTechRoute && !isGoingToLogin && !isGoingToPublicLegal) {
      // Assigned learning only. /lms/courses and /lms/progress stay blocked.
      const learning = technicianLearningLocation(to)
      if (learning) {
        console.log('[Router] Technician session, opening assigned learning:', to.path, '->', learning.path)
        next(learning)
      } else {
        console.log('[Router] Technician session but accessing non-tech route, redirecting:', to.path, '-> /technician')
        next('/technician')
      }
    } else {
      next()
    }
  } else if (!isTechnicianSession && authStore.isAuthenticated && to.path.startsWith('/technician')) {
    console.log('[Router] Non-technician accessing tech route, redirecting to', officeHomePath(authStore))
    next(officeHomePath(authStore))
  } else if (to.meta.permission && authStore.isAuthenticated) {
    if (!authStore.hasPermission(to.meta.permission)) {
      // Try to refresh permissions once (they may be stale after backend RBAC changes)
      if (!window.__permRefreshAttempted) {
        window.__permRefreshAttempted = true
        console.log('[Router] Permission missing, refreshing from server once:', to.meta.permission)
        authStore.refreshPermissions()
          .then(() => {
            if (authStore.hasPermission(to.meta.permission)) {
              next()
            } else if (to.path === '/dashboard') {
              const home = officeHomePath(authStore)
              if (home !== '/dashboard') next(home)
              else next()
            } else {
              console.log('[Router] Still denied after refresh:', to.meta.permission)
              next(officeHomePath(authStore))
            }
          })
          .catch(() => {
            const home = officeHomePath(authStore)
            if (to.path === '/dashboard' && home === '/dashboard') next()
            else if (to.path === '/dashboard') next(home)
            else next(home)
          })
        return
      }
      console.log('[Router] Permission denied:', to.meta.permission, 'for route:', to.path)
      // Prevent infinite loop: if already going to /dashboard and no permission, let through
      if (to.path === '/dashboard') {
        const home = officeHomePath(authStore)
        if (home !== '/dashboard') {
          next(home)
        } else {
          console.log('[Router] Allowing dashboard access despite missing permission to prevent redirect loop')
          next()
        }
      } else {
        next(officeHomePath(authStore))
      }
    } else {
      next()
    }
  } else {
    next()
  }
})

// Platform HQ guard. The shared infrastructure, platform economics, cross-tenant
// security/audit and tenant-onboarding screens belong to the SINGLE Platform HQ
// company (domains.is_platform=1) — they are reachable ONLY while the user is
// working inside that company, and hidden from every client company (the infra is
// shared; clients have no own servers). Gated by the CURRENT company via
// authStore.isPlatformHQ (mirrors backend IsPlatformHQDomain -> 404). Users in a
// client company (incl. impersonation) are silently redirected to the dashboard.
const PLATFORM_ONLY_PREFIXES = ['/app-admin', '/admin/intake', '/admin/billing']
const PLATFORM_ONLY_PATHS = new Set([
  '/settings/system-health',
  '/settings/server-performance',
  '/settings/error-monitor',
  '/settings/cron-jobs',
  '/dashboard/technical',
])
function isPlatformOnlyRoute(to) {
  if (to.meta && to.meta.requiresPlatform) return true
  if (PLATFORM_ONLY_PATHS.has(to.path)) return true
  return PLATFORM_ONLY_PREFIXES.some(p => to.path === p || to.path.startsWith(p + '/'))
}
router.beforeEach((to, from, next) => {
  if (to.meta && to.meta.isPortalRoute) return next()
  const authStore = useAuthStore()
  if (!authStore.isAuthenticated) return next()
  if (isPlatformOnlyRoute(to) && !authStore.isPlatformHQ) {
    console.log('[Router] Platform-HQ-only route blocked (not in HQ company):', to.path)
    if (to.path === '/dashboard') return next()
    return next('/dashboard')
  }
  next()
})

// White-label entitlement guard (runs after the main auth guard).
// Blocks routes whose feature is disabled for the current tenant and redirects
// to the dashboard. No-op for normal domains (all features enabled) and for
// unmapped routes. Defense-in-depth only — the backend also enforces features.
router.beforeEach((to, from, next) => {
  if (to.meta && to.meta.isPortalRoute) return next()
  const authStore = useAuthStore()
  if (!authStore.isAuthenticated) return next()
  const feature = routeToFeature(to.path)
  if (feature && !authStore.hasFeature(feature)) {
    console.log('[Router] Feature not enabled for tenant:', feature, '— route:', to.path)
    if (to.path === '/dashboard') return next()
    return next('/dashboard')
  }
  next()
})

// AP-7-00368/00369: per-company empty-CRM-page guard. A company can hide the
// Lead Sources page and/or the Flow Builder tab (empty screens for businesses
// that do not use them). This guard stops a deep link to one of those pages
// from rendering the blank screen for such a company, sending them to Customers
// instead. Scoped to just those CRM paths, so it never adds cost to any other
// navigation; every company that did NOT opt in is unaffected. Defense-in-depth
// — CRMView also hides the tabs and falls back to Customers on its own.
const CRM_EMPTY_PAGE_PATHS = new Set(['/crm/leads', '/crm/leads/sources', '/crm/leads/flows'])
const crmCompanyCtx = useCompanyContext()
router.beforeEach(async (to, from, next) => {
  if (to.meta && to.meta.isPortalRoute) return next()
  const authStore = useAuthStore()
  if (!authStore.isAuthenticated) return next()
  if (!CRM_EMPTY_PAGE_PATHS.has(to.path)) return next()
  let ctx = crmCompanyCtx.context.value
  if (!ctx) {
    try { ctx = await crmCompanyCtx.load() } catch { ctx = null }
  }
  if (ctx) {
    const leadSourcesHidden = ctx.crm_lead_sources_hidden === true
    const flowBuilderHidden = ctx.crm_flow_builder_hidden === true
    if ((to.path === '/crm/leads' || to.path === '/crm/leads/sources') && leadSourcesHidden) {
      console.log('[Router] Lead Sources hidden for this company — redirecting to /crm/customers')
      return next('/crm/customers')
    }
    if (to.path === '/crm/leads/flows' && flowBuilderHidden) {
      console.log('[Router] Flow Builder hidden for this company — redirecting to /crm/customers')
      return next('/crm/customers')
    }
  }
  next()
})

// TV-1-00345: Crew Vehicles + Route Planner are cleaning-preset fleet tools.
// Hide deep links for appliance-repair companies the same way the nav does.
const FLEET_PATH_PREFIX = '/fleet/'
const fleetCompanyCtx = useCompanyContext()
router.beforeEach(async (to, from, next) => {
  if (to.meta && to.meta.isPortalRoute) return next()
  if (!to.path.startsWith(FLEET_PATH_PREFIX)) return next()
  const authStore = useAuthStore()
  if (!authStore.isAuthenticated) return next()
  let ctx = fleetCompanyCtx.context.value
  if (!ctx) {
    try { ctx = await fleetCompanyCtx.load() } catch { ctx = null }
  }
  const preset = ctx?.industry_preset_code || authStore.domain?.industry_preset_code || null
  if (resolveFlowField('menu.fleet', ctx, preset)) return next()
  console.log('[Router] Fleet menu hidden for this company — redirecting to /dispatch')
  return next('/dispatch')
})

// Appliance-only screens (parts, inventory, brands, parts reports). A cleaning
// company already hides these in the menu via Company Flow; this stops a deep
// link from opening them. Appliance-repair companies keep every route.
const APPLIANCE_PARTS_PREFIXES = ['/operations', '/assets']
const applianceCompanyCtx = useCompanyContext()

function applianceScreenField(path) {
  if (path === '/jobs/settings/brands') return 'field.appliance_brands'
  if (path.startsWith('/reports/parts')) return 'finance.parts'
  if (APPLIANCE_PARTS_PREFIXES.some(p => path === p || path.startsWith(p + '/'))) return 'menu.inventory_parts'
  return null
}

router.beforeEach(async (to, from, next) => {
  if (to.meta && to.meta.isPortalRoute) return next()
  const fieldKey = applianceScreenField(to.path)
  if (!fieldKey) return next()
  const authStore = useAuthStore()
  if (!authStore.isAuthenticated) return next()
  let ctx = applianceCompanyCtx.context.value
  if (!ctx) {
    try { ctx = await applianceCompanyCtx.load() } catch { ctx = null }
  }
  const preset = ctx?.industry_preset_code || authStore.domain?.industry_preset_code || null
  if (resolveFlowField(fieldKey, ctx, preset)) return next()
  console.log('[Router] Appliance-only screen hidden for this company:', to.path)
  return next('/dashboard')
})

// First-login mandatory profile-completion gate (Aug 11 P18). If the logged-in
// user is linked to an HR employee whose self-service profile is incomplete,
// force them to the profile tab of "My Requests" until it is filled. Fails open
// on any error (users without HR access are never gated). Runs after auth.
const PROFILE_PERMISSIONS_REFRESH_RETRY_MS = 30_000
let lastProfilePermissionsRefreshAt = 0
let lastProfilePermissionsIdentity = ''
router.beforeEach(async (to, from, next) => {
  if (to.meta && to.meta.isPortalRoute) return next()
  const authStore = useAuthStore()
  if (!authStore.isAuthenticated) return next()
  // Technician sessions run their own restricted flow; never gate them here.
  try {
    if (localStorage.getItem('is_technician_session') === 'true') return next()
  } catch { /* unavailable storage is handled by native session recovery */ }
  // Platform admins / owners are never gated by the employee first-login profile
  // completion flow: they log in as themselves to administer the platform, not
  // as an HR employee record that may be linked to their account. Clear any
  // stale cached gate flag so a superadmin is unblocked immediately, without a
  // re-login. Regular employees and managers are still gated.
  // Stays ahead of the hydration check below: hasPermission() has no superadmin
  // bypass, so gating this branch on hr.me.view would stop clearing the flag for
  // an admin who lacks it, and MyHrPortalView would keep showing them the
  // must-complete state. An unhydrated store has no user, so this is a no-op then.
  const gu = authStore.user
  if (gu && (gu.is_superuser || gu.platform_access)) {
    if (authStore.mustCompleteProfile) authStore.clearProfileGate()
    return next()
  }
  // Always let the profile page itself (and auth pages) through. Stays ahead of
  // the permission hydration below, which awaits the network: an office user who
  // lost their rights cache would otherwise wait out a request — a full axios
  // timeout when offline — before Sign Out was allowed to navigate.
  if (PROFILE_GATE_ALLOWED_PREFIXES.some(p => to.path === p || to.path.startsWith(p + '/'))) {
    return next()
  }
  let profileGateBlockReason = profileCompletionGateBlockReason(authStore)
  if (profileGateBlockReason === 'permissions are not hydrated') {
    const identity = `${authStore.user?.id || ''}:${authStore.domain?.id || ''}`
    if (identity !== lastProfilePermissionsIdentity) {
      lastProfilePermissionsIdentity = identity
      lastProfilePermissionsRefreshAt = 0
    }
    const now = Date.now()
    if (now - lastProfilePermissionsRefreshAt < PROFILE_PERMISSIONS_REFRESH_RETRY_MS) {
      console.warn('[Router] Profile gate permissions refresh is cooling down, allowing navigation')
      return next()
    }
    lastProfilePermissionsRefreshAt = now
    try {
      // The first navigation happens before App.vue's mount-time background
      // refresh. Wait here immediately, then use a bounded retry interval if the
      // server is unavailable so every route change does not repeat the request.
      await authStore.refreshPermissions()
      profileGateBlockReason = profileCompletionGateBlockReason(authStore)
    } catch (e) {
      console.warn('[Router] Profile gate permissions refresh failed, allowing navigation', e)
      return next()
    }
  }
  if (profileGateBlockReason) {
    console.warn(`[Router] Profile gate skipped — ${profileGateBlockReason}`)
    return next()
  }
  try {
    const must = await authStore.checkProfileCompletion()
    if (must) {
      console.log('[Router] Profile incomplete — redirecting to profile completion gate')
      return next({ path: '/hr/my-portal', query: { complete: '1' } })
    }
  } catch (e) {
    // Fail open — never lock the user out because the status check errored.
    console.warn('[Router] Profile gate check failed, allowing navigation', e)
  }
  next()
})

// Auto-reload when JS chunk fails to load (stale Service Worker cache after deploy)
router.onError((error, to) => {
  const chunkFailed = (
    error.message?.includes('Failed to fetch dynamically imported module') ||
    error.message?.includes('Loading chunk') ||
    error.message?.includes('Loading CSS chunk') ||
    error.message?.includes('Unable to preload CSS') ||
    error.message?.includes('is not a valid JavaScript MIME type') ||
    error.message?.includes('Importing a module script failed')
  )

  if (chunkFailed) {
    const reloadKey = 'chunk_reload_' + (to?.fullPath || window.location.pathname)
    const lastReload = sessionStorage.getItem(reloadKey)
    const now = Date.now()

    // Prevent infinite reload loop: only reload once per path per 30 seconds
    if (!lastReload || (now - parseInt(lastReload, 10)) > 30000) {
      sessionStorage.setItem(reloadKey, String(now))
      console.warn('[Router] Chunk load failed, reloading page:', error.message)
      window.location.assign(to?.fullPath || window.location.pathname)
      return
    }
    console.error('[Router] Chunk load failed after recent reload, not reloading again:', error.message)
  }
})

export default router

